import { useEffect, useMemo, useRef, useState } from "react";
import posthog from "posthog-js";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Plus,
  Trash2,
  ChevronDown,
  Sparkles,
  AlertCircle,
  CheckCircle2,
  PlayCircle,
  Loader2,
  ArrowRight,
  RotateCcw,
  OctagonX,
  Copy,
  Lock,
  Info,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import type {
  RealityCheckMode,
  V2RealityCheck,
  V2SprintSubmission,
} from "@/v2/hooks/useV2Sprint";
import { supabase } from "@/integrations/supabase/client";
import {
  EXPERIMENT_PRESETS,
  buildHypothesisSuggestions,
  getExperimentPreset,
  guessExperimentFromRC,
  buildMetricsToCollect,
  buildEvidenceToCollect,
  normalizeExperimentId,
  getNextActions,
  buildStep4MetricDefinitions,
  parseThreshold,
  evaluateEvidenceQuality,
  computeStep4Gaps,
  computeDerivedMetrics,
  mapLegacyToMetricResults,
  buildLegacyMetricsMirror,
  buildLegacyEvidenceLinksMirror,
  buildLegacyRealQuotesMirror,
  parseMinimumSample,
  deriveSuggestedVerdict,
  mapLegacyVerdict,
  verdictToLegacy,
  buildLegacyRationale,
  buildLegacyNextStep,
  buildStep5DecisionSummary,
  type SuggestedItem,
  type ChecklistSeedSource,
  type ExperimentSnapshotLike,
  type NextAction,
  type MetricResult,
  type ThresholdAssessment,
  type QualitativeEvidence,
  type TypedEvidenceLink,
  type TypedEvidenceLinkType,
  type EvidenceQualityAssessment,
  type SuggestedVerdictResult,
  type Step5Verdict,
  type Step5ChangeDimension,
  type Step5StopDisposition,
} from "@/v2/lib/sprintTemplates";

export interface StepDraft {
  content: Record<string, any>;
  evidenceLinks: string[];
}

export function validateLinks(links: string[]): { ok: string[]; invalid: string[] } {
  const ok: string[] = [];
  const invalid: string[] = [];
  for (const raw of links) {
    const v = raw.trim();
    if (!v) continue;
    try {
      const u = new URL(v.startsWith("http") ? v : `https://${v}`);
      if (u.hostname.includes(".")) ok.push(u.toString());
      else invalid.push(v);
    } catch {
      invalid.push(v);
    }
  }
  return { ok, invalid };
}

interface Props {
  step: number;
  submission: V2SprintSubmission | undefined;
  realityCheck: V2RealityCheck | null;
  realityCheckMode: RealityCheckMode;
  allSubmissions: V2SprintSubmission[];
  readOnly: boolean;
  saving: boolean;
  submitLabel: string;
  isStepCompleted?: boolean;
  sprintStartedAt?: string | null;
  sprintId?: string | null;
  onSubmit: (draft: StepDraft) => void | Promise<void>;
  onSaveDraft?: (
    draft: StepDraft,
    opts?: { unlockCall?: boolean },
  ) => void | Promise<void>;
  onGoToStep?: (step: number) => void;
}

function StepIntroCard({ text }: { text: string }) {
  return (
    <div className="rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-slate-400 leading-relaxed">
      <span className="text-[10px] uppercase tracking-widest text-slate-500 font-medium block mb-1">Cosa fai qui</span>
      {text}
    </div>
  );
}

export default function SprintStepForm({
  step,
  submission,
  realityCheck,
  realityCheckMode,
  allSubmissions,
  readOnly,
  saving,
  submitLabel,
  isStepCompleted = false,
  sprintStartedAt = null,
  sprintId = null,
  onSubmit,
  onSaveDraft,
  onGoToStep,
}: Props) {

  const step2DraftKey =
    step === 2 && sprintId ? `proof_sprint_step2_draft_${sprintId}` : null;



  const initial = useMemo<StepDraft>(
    () => {
      const rawContent = (submission?.content as Record<string, any>) ?? {};
      const content = { ...rawContent };
      // Step 1 sanitization: cancella top_hypothesis se è chiaramente
      // rischio grezzo/legacy (identico a main_risk o frase che inizia
      // per "Il rischio principale è…", "Rischio principale…",
      // "Il rischio maggiore è…"). Le ipotesi reali dell'utente
      // (con numeri/target/azioni) restano intatte.
      if (step === 1 && typeof content.top_hypothesis === "string") {
        const v = content.top_hypothesis.trim();
        if (v) {
          const rawRisk = (realityCheck?.main_risk ?? "").trim();
          const lower = v.toLowerCase();
          const isLegacyRiskText =
            (!!rawRisk && v === rawRisk) ||
            lower.startsWith("il rischio principale è") ||
            lower.startsWith("rischio principale") ||
            lower.startsWith("il rischio maggiore è");
          if (isLegacyRiskText) {
            content.top_hypothesis = "";
          }
        }
      }
      // Step 2 draft restore from localStorage (only if no completed submission).
      // Merges non-empty stored fields over the initial content, so the user
      // doesn't lose in-progress input when navigating between steps.
      if (step === 2 && step2DraftKey && !submission) {
        try {
          const raw = localStorage.getItem(step2DraftKey);
          if (raw) {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object" && parsed.content) {
              Object.assign(content, parsed.content);
            }
          }
        } catch {}
      }
      return {
        content,
        evidenceLinks: submission?.evidence_links ?? [],
      };
    },
    [submission?.id, submission?.updated_at, step, realityCheck?.main_risk, step2DraftKey]
  );

  const [draft, setDraft] = useState<StepDraft>(initial);
  const [error, setError] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  // Reset bannerDismissed when switching step or submission
  const lastKeyRef = useRef<string>("");
  const key = `${step}:${submission?.id ?? "new"}`;
  if (lastKeyRef.current !== key) {
    lastKeyRef.current = key;
  }

  useEffect(() => {
    setDraft(initial);
    setError(null);
    setBannerDismissed(false);
  }, [initial]);

  // Step 2: persist draft to localStorage on every change (until submission exists).
  useEffect(() => {
    if (step !== 2 || !step2DraftKey) return;
    if (submission) return;
    try {
      localStorage.setItem(
        step2DraftKey,
        JSON.stringify({ content: draft.content }),
      );
    } catch {}
  }, [step, step2DraftKey, submission, draft.content]);

  const set = (key: string, value: any) =>
    setDraft((d) => ({ ...d, content: { ...d.content, [key]: value } }));

  const disabled = readOnly || saving;

  // Mismatch detection between saved submission context and current RC
  const savedCtxId: string | null = submission?.content
    ? (submission.content as any).reality_check_context_id ?? null
    : null;
  const currentRcId = realityCheck?.id ?? null;
  const ctxMismatch =
    !!submission && !!currentRcId && savedCtxId !== currentRcId && !readOnly;

  // Step 1 content drift: saved top_hypothesis no longer matches current RC suggestions
  const step1Drift = (() => {
    if (step !== 1 || readOnly || !submission || !realityCheck) return false;
    const c = (submission.content as any) ?? {};
    const top = typeof c.top_hypothesis === "string" ? c.top_hypothesis.trim() : "";
    if (!top) return false;
    const currentSuggestions = buildHypothesisSuggestions(realityCheck);
    const currentIds = new Set(currentSuggestions.map((s) => s.id));
    const currentLabels = new Set(
      currentSuggestions.map((s) => s.hypothesis.trim().toLowerCase()),
    );
    const savedIds: string[] = Array.isArray(c.suggested_hypotheses)
      ? c.suggested_hypotheses
      : [];
    if (savedIds.length > 0) {
      // Suggestions tracked: drift if none of saved ids are present today
      return !savedIds.some((id) => currentIds.has(id));
    }
    // Legacy submission with no tracking: drift if top_hypothesis isn't a current label
    return !currentLabels.has(top.toLowerCase());
  })();

  const mismatch = ctxMismatch || step1Drift;

  const regenerate = () => {
    setDraft((d) => {
      const c = { ...d.content };
      let links = d.evidenceLinks;
      switch (step) {
        case 1:
          delete c.top_hypothesis;
          delete c.selected_hypotheses;
          delete c.custom_hypotheses;
          delete c.suggested_hypotheses;
          break;
        case 2: {
          delete c.selected_experiment;
          delete c.target;
          delete c.channel;
          delete c.action;
          delete c.primary_metric;
          delete c.success_threshold;
          delete c.duration_days;
          delete c.minimum_sample;
          delete c.secondary_metrics;
          delete c.invalid_signals;
          delete c.notes;
          // Legacy cleanup
          delete c.custom_experiment_description;
          delete c.expected_outcome;
          // Prefill from current RC when possible
          const sugg = guessExperimentFromRC(realityCheck);
          if (sugg) {
            c.selected_experiment = sugg;
            const preset = getExperimentPreset(sugg);
            c.success_threshold =
              str(realityCheck?.minimum_success_threshold)
                ? realityCheck!.minimum_success_threshold!
                : preset?.defaultThreshold ?? "";
          } else if (str(realityCheck?.minimum_success_threshold)) {
            c.success_threshold = realityCheck!.minimum_success_threshold!;
          }
          if (str(realityCheck?.target_summary)) {
            c.target = realityCheck!.target_summary!;
          }
          if (str(realityCheck?.success_metric)) {
            c.primary_metric = realityCheck!.success_metric!;
          }
          if (str(realityCheck?.experiment_48h)) {
            c.notes = realityCheck!.experiment_48h!;
          }
          break;
        }
        case 3:
          delete c.selected_checklist;
          delete c.time_budget;
          delete c.protocol_notes;
          break;
        case 4:
          delete c.metrics;
          delete c.real_quotes;
          delete c.objections;
          delete c.insights;
          delete c.notes;
          delete c.evidence_strength_notes;
          links = [];
          break;
        case 5:
          delete c.verdict;
          delete c.rationale;
          delete c.next_step;
          delete c.threshold_recap;
          delete c.results_recap;
          break;
      }
      return { content: c, evidenceLinks: links };
    });
    setBannerDismissed(true);
    toast.success(
      "Suggerimenti aggiornati dal Reality Check attuale. Salva per confermare le modifiche."
    );
  };

  // Validation rules per step (mandatory fields)
  const validate = (d: StepDraft): string | null => {
    const c = d.content;
    switch (step) {
      case 1:
        if (!str(c.top_hypothesis)) return "Indica l'ipotesi #1 da testare.";
        return null;
      case 2:
        // Step 2 flow è centrato sul Base Prompt Lovable auto-generato.
        // Il founder deve solo aver definito almeno l'ipotesi e la soglia.
        if (!str(c.suggested_threshold) && !str(c.success_threshold))
          return "Indica la soglia minima di successo.";
        return null;
      case 3:
        // Step 3 manages its own staged validation (planning → running → handoff)
        // and submits via its internal AlertDialog. No outer-form validation.
        return null;
      case 4:
        // Step 4 manages its own staged save (collecting → ready_for_verdict)
        // and submits via its internal AlertDialog. No outer-form validation.
        return null;
      case 5:
        // Step 5 manages its own internal CTAs (Salva bozza + Conferma verdetto)
        // through an inline AlertDialog. No outer-form validation.
        return null;
      default:
        return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (disabled) return;
    const err = validate(draft);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    // Stamp the current Reality Check context onto the saved content
    const stamped: StepDraft = {
      ...draft,
      content: {
        ...draft.content,
        reality_check_context_id: currentRcId,
        reality_check_context_mode: realityCheckMode,
      },
    };
    try {
      await onSubmit(stamped);
      try {
        posthog.capture("sprint_step_completed", { step });
      } catch {}
      // On successful Step 2 completion, clear the persisted draft.
      if (step === 2 && step2DraftKey) {
        try {
          localStorage.removeItem(step2DraftKey);
        } catch {}
      }
    } catch {
      // Errors are surfaced upstream via toast; keep draft in localStorage.
    }
  };

  const useAsTopHypothesis = (text: string) => set("top_hypothesis", text);

  // Step 3 helpers — staged save (no completion) and final completion.
  // Both stamp the current RC context, both apply the patch to local draft
  // synchronously and pass the same merged content to the mutation so the
  // server never receives a stale snapshot.
  const stampRc = (c: Record<string, any>) => ({
    ...c,
    reality_check_context_id: currentRcId,
    reality_check_context_mode: realityCheckMode,
  });

  const saveStep3Draft = async (
    patch: Record<string, any>,
    opts?: { unlockCall?: boolean },
  ) => {
    if (!onSaveDraft) return;
    const nextContent = { ...draft.content, ...patch };
    setDraft((d) => ({ ...d, content: { ...d.content, ...patch } }));
    await onSaveDraft(
      { content: stampRc(nextContent), evidenceLinks: draft.evidenceLinks },
      opts,
    );
  };

  const completeStep3 = async (patch: Record<string, any>) => {
    const nextContent = { ...draft.content, ...patch };
    setDraft((d) => ({ ...d, content: { ...d.content, ...patch } }));
    await onSubmit({
      content: stampRc(nextContent),
      evidenceLinks: draft.evidenceLinks,
    });
  };


  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {mismatch && !bannerDismissed && (
        <div className="rounded-xl border border-amber-400/40 bg-amber-500/10 p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-300 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-100">
              Queste risposte erano state salvate con un contesto Reality Check diverso.
              Puoi mantenerle oppure rigenerare i suggerimenti dal Reality Check attuale.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setBannerDismissed(true)}
            >
              Mantieni risposte
            </Button>
            <Button
              type="button"
              size="sm"
              className="bg-primary hover:bg-primary/90"
              onClick={regenerate}
            >
              Rigenera suggerimenti
            </Button>
          </div>
        </div>
      )}


      {step === 1 && (
        <Step1
          rc={realityCheck}
          content={draft.content}
          set={set}
          disabled={disabled}
          onUseAsTopHypothesis={useAsTopHypothesis}
        />
      )}
      {step === 2 && (
        <Step2
          rc={realityCheck}
          content={draft.content}
          set={set}
          disabled={disabled}
          allSubmissions={allSubmissions}
          onGoToStep={onGoToStep}
        />
      )}

      {step === 3 && (
        <Step3
          content={draft.content}
          set={set}
          disabled={disabled}
          saving={saving}
          readOnly={readOnly}
          isStepCompleted={isStepCompleted}
          allSubmissions={allSubmissions}
          saveDraft={saveStep3Draft}
          completeStep={completeStep3}
          onGoToStep={onGoToStep}
          realityCheck={realityCheck}
        />
      )}
      {step === 4 && (
        <Step4
          content={draft.content}
          set={set}
          disabled={disabled}
          saving={saving}
          readOnly={readOnly}
          isStepCompleted={isStepCompleted}
          allSubmissions={allSubmissions}
          links={draft.evidenceLinks}
          onLinksChange={(links) => setDraft((d) => ({ ...d, evidenceLinks: links }))}
          saveDraft={saveStep3Draft}
          completeStep={completeStep3}
        />
      )}
      {step === 5 && (
        <Step5
          content={draft.content}
          set={set}
          disabled={disabled}
          readOnly={readOnly}
          isStepCompleted={!!isStepCompleted}
          saving={saving}
          allSubmissions={allSubmissions}
          sprintStartedAt={sprintStartedAt}
          saveDraft={saveStep3Draft}
          completeStep={completeStep3}
        />
      )}

      {error && (
        <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
          {error}
        </p>
      )}

      {!readOnly && step !== 3 && step !== 4 && step !== 5 && (
        <div className="flex justify-end pt-2">
          <Button
            type="submit"
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
          >
            {saving ? "Salvataggio…" : submitLabel}
          </Button>
        </div>
      )}
    </form>
  );
}

// ---------- helpers ----------
const str = (v: any) => typeof v === "string" && v.trim().length > 0;
const isNum = (v: any) =>
  typeof v === "number" ? !isNaN(v) : typeof v === "string" && v.trim() !== "" && !isNaN(Number(v));

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-slate-100">{label}</Label>
      {hint && <p className="text-xs text-slate-300">{hint}</p>}
      {children}
    </div>
  );
}

const inputClass =
  "bg-white/[0.06] border-white/15 text-slate-100 placeholder:text-slate-400 focus-visible:ring-secondary/50";

function SuggestionCard({
  item,
  selected,
  onToggle,
  disabled,
}: {
  item: SuggestedItem;
  selected: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={cn(
        "text-left rounded-xl border p-3 transition-all w-full",
        selected
          ? "border-primary/60 bg-primary/10"
          : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <p className="text-sm font-medium text-slate-100">{item.label}</p>
      {item.description && (
        <p className="text-xs text-slate-300 mt-1">{item.description}</p>
      )}
    </button>
  );
}

function SuggestionsBlock({
  title,
  items,
  selectedIds,
  onToggle,
  disabled,
  rcContext,
}: {
  title: string;
  items: SuggestedItem[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  disabled: boolean;
  rcContext: boolean;
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-secondary" />
        <p className="text-xs uppercase tracking-wider text-secondary font-medium">
          {title}
        </p>
        {rcContext ? (
          <span className="text-[10px] text-slate-300">(dal tuo Reality Check)</span>
        ) : (
          <span className="text-[10px] text-slate-300">(suggerimenti generici)</span>
        )}
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {items.map((it) => (
          <SuggestionCard
            key={it.id}
            item={it}
            selected={selectedIds.includes(it.id)}
            onToggle={() => onToggle(it.id)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}

// ---------- Step 1 ----------
function Step1({
  rc,
  content,
  set,
  disabled,
  onUseAsTopHypothesis,
}: {
  rc: V2RealityCheck | null;
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  disabled: boolean;
  onUseAsTopHypothesis: (text: string) => void;
}) {
  const suggestions = useMemo(() => buildHypothesisSuggestions(rc), [rc?.id]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const firstRecommendedIndex = suggestions.findIndex((it) => !it.isRaw);

  // La sanitizzazione del rischio grezzo/legacy avviene già nel parent
  // (useMemo `initial`), così resta valida anche dopo refetch.

  const useThis = (text: string) => {
    onUseAsTopHypothesis(text);
    set(
      "suggested_hypotheses",
      suggestions.map((s) => s.id)
    );
    // Focus la textarea così l'utente può modificare subito.
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (el) {
        el.focus();
        const len = el.value.length;
        el.setSelectionRange(len, len);
      }
    });
    toast.success("Ipotesi inserita. Puoi modificarla prima di continuare.");
  };


  return (
    <div className="space-y-5">
      <StepIntroCard text="Ogni idea ha un'assunzione che, se fosse sbagliata, farebbe crollare tutto. Questo step serve a identificarla con precisione e a trasformarla in qualcosa di testabile. Non si tratta di validare l'idea in generale — si tratta di trovare la crepa più pericolosa e andarci contro di proposito." />
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-secondary" />
          <p className="text-xs uppercase tracking-wider text-secondary font-medium">
            Spunti dal tuo Reality Check
          </p>
          {rc ? (
            <span className="text-[10px] text-slate-300">(dal tuo Reality Check)</span>
          ) : (
            <span className="text-[10px] text-slate-300">(suggerimenti generici)</span>
          )}
        </div>
        <p className="text-xs text-slate-300">
          Solo ispirazione. Scegline una con "Usa questa", oppure scrivi la tua nel campo sotto.
        </p>
        <div className="grid gap-2 sm:grid-cols-2">
          {suggestions.map((it, idx) => (
            <div
              key={it.id}
              className={cn(
                "rounded-xl border p-3 space-y-2 transition-all",
                it.isRaw
                  ? "border-amber-400/30 bg-amber-500/5"
                  : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
                disabled && "opacity-50"
              )}
            >
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <p
                    className={cn(
                      "text-[11px] uppercase tracking-wider font-medium",
                      it.isRaw ? "text-amber-300" : "text-secondary"
                    )}
                  >
                    {it.title}
                  </p>
                  {idx === firstRecommendedIndex && !it.isRaw && (
                    <span className="inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] uppercase tracking-wider text-emerald-300 font-medium">
                      Consigliato
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-100 leading-snug">
                  {it.hypothesis}
                </p>
                <p className="text-xs text-slate-300">
                  <span className="font-medium text-slate-300">Perché è rischiosa: </span>
                  {it.why}
                </p>
              </div>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => useThis(it.hypothesis)}
                  className="text-xs text-secondary hover:text-secondary/80 font-medium"
                >
                  Usa questa →
                </button>
              )}
            </div>
          ))}
        </div>

      </div>
      <Field
        label="L'ipotesi più rischiosa che vuoi testare ora"
        hint="Una sola frase, falsificabile, con soggetto, azione misurabile e numero. Niente aggettivi vaghi."
      >
        <Textarea
          ref={textareaRef}
          rows={3}
          disabled={disabled}
          className={inputClass}
          value={content.top_hypothesis ?? ""}
          onChange={(e) => set("top_hypothesis", e.target.value)}
          placeholder="Es: Almeno il 5% dei founder early-stage che vedono la landing lascia l'email per provare il protocollo di validazione in 14 giorni."
        />
      </Field>
    </div>
  );
}

// ---------- Step 2 ----------
function getChannelCopy(experimentId: string | undefined | null): {
  hint: string;
  placeholder: string;
} {
  switch (experimentId) {
    case "interviste":
      return {
        hint: "Dove troverai e contatterai le persone? Es. LinkedIn, telefono, community, referral.",
        placeholder: "Es. LinkedIn, community Slack, referral",
      };
    case "landing_waitlist":
      return {
        hint: "Da dove arriveranno le persone sulla pagina? Es. DM, email, community, traffico organico o ads.",
        placeholder: "Es. DM LinkedIn, post community, ads",
      };
    case "concierge_test":
      return {
        hint: "Dove proporrai ed erogherai manualmente la prova? Es. WhatsApp, call, email, incontro diretto.",
        placeholder: "Es. WhatsApp, call Zoom, email",
      };
    case "fake_door":
    case "smoke_test":
      return {
        hint: "Dove mostrerai la proposta o CTA? Es. sito, post, community, newsletter.",
        placeholder: "Es. homepage, post LinkedIn, newsletter",
      };
    default:
      return {
        hint: "Dove eroghi il test.",
        placeholder: "Es. LinkedIn DM, community Slack, lista email",
      };
  }
}

function getExperimentLabel(id: string | undefined | null): string {
  const p = getExperimentPreset(id);
  return p?.label ?? "";
}

function parseDaysFromThreshold(s: string | undefined | null): string {
  if (!s) return "";
  const m = s.match(/(\d+)\s*giorn/i);
  return m ? m[1] : "";
}

function thresholdWarnings(v: string): string[] {
  const warnings: string[] = [];
  if (!v.trim()) return warnings;
  if (!/\d/.test(v)) {
    warnings.push("Aggiungi un numero per rendere la soglia verificabile.");
  }
  const timeRegex = /\b(giorn|settiman|mes|or[ae]|entro|week|day|month|hour)\w*/i;
  if (!timeRegex.test(v)) {
    warnings.push("Indica entro quando misurerai il risultato.");
  }
  return warnings;
}

type QualityCheckItem = { pass: boolean; note: string };
type LovablePromptResult = {
  base_prompt: string;
  quality_checklist: {
    target_clarity: QualityCheckItem;
    real_problem: QualityCheckItem;
    differentiation: QualityCheckItem;
    decision_criteria: QualityCheckItem;
    actionable_output: QualityCheckItem;
    validation_type: string;
    risk_level: "low" | "medium" | "high";
    risk_reason: string;
  };
  critical_review: { strong: string; weak: string; missing?: string };
  suggested_metric: string;
  suggested_threshold: string;
  suggested_failure_criteria: string;
};

const LOVABLE_INVITE_URL = "https://lovable.dev/invite/MXUNQFR";

function Step2({
  rc,
  content,
  set,
  disabled,
  allSubmissions,
  onGoToStep,
}: {
  rc: V2RealityCheck | null;
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  disabled: boolean;
  allSubmissions: V2SprintSubmission[];
  onGoToStep?: (step: number) => void;
}) {
  // Ipotesi dello Step 1
  const step1Submission = allSubmissions.find((s) => s.step_number === 1);
  const topHypothesis: string =
    (step1Submission?.content as any)?.top_hypothesis?.trim?.() ?? "";

  const [lovableResult, setLovableResult] = useState<LovablePromptResult | null>(
    (content.lovable_prompt_result as LovablePromptResult) ?? null
  );
  const [lovableLoading, setLovableLoading] = useState(false);
  const [lovableError, setLovableError] = useState<string | null>(null);
  const [promptCopied, setPromptCopied] = useState(false);
  const autoRunRef = useRef(false);

  const generateLovablePrompt = async () => {
    setLovableLoading(true);
    setLovableError(null);
    try {
      const fallbackThreshold = "Da definire in base al test";
      const fallbackTarget = "Da definire in base al Reality Check";
      const { data, error } = await supabase.functions.invoke("v2-sprint-lovable-prompt", {
        body: {
          hypothesis: topHypothesis && topHypothesis.length >= 3 ? topHypothesis : "Ipotesi da testare",
          success_threshold:
            (str(content.success_threshold) && content.success_threshold) ||
            (str(rc?.minimum_success_threshold) && rc!.minimum_success_threshold!) ||
            (str(rc?.success_metric) && rc!.success_metric!) ||
            fallbackThreshold,
          target: (str(rc?.target_summary) && rc!.target_summary!) || fallbackTarget,
          rc_idea: rc?.idea_summary ?? "",
          rc_solution: rc?.experiment_48h ?? "",
          rc_problem: rc?.main_risk ?? "",
          rc_main_risk: rc?.main_risk ?? "",
        },
      });

      if (error || !data) throw new Error("generation_failed");
      const result = data as LovablePromptResult;
      setLovableResult(result);
      set("lovable_prompt_result", result);
      set("base_prompt", result.base_prompt);
      // Pre-fill metric fields only if empty
      if (!str(content.suggested_metric)) set("suggested_metric", result.suggested_metric);
      if (!str(content.suggested_threshold)) set("suggested_threshold", result.suggested_threshold);
      if (!str(content.suggested_failure_criteria))
        set("suggested_failure_criteria", result.suggested_failure_criteria);
      if (result.suggested_threshold) {
        const hadPreviousThreshold = str(content.success_threshold);
        set("success_threshold", result.suggested_threshold);
        if (hadPreviousThreshold) {
          toast.success("Soglia di successo aggiornata per la landing page.");
        }
      }

    } catch {
      setLovableError("Non è stato possibile generare il prompt. Riprova.");
    } finally {
      setLovableLoading(false);
    }
  };

  // Auto-generate at mount if we don't already have a result and step is not locked
  useEffect(() => {
    if (autoRunRef.current) return;
    if (disabled) return;
    if (lovableResult) return;
    if (content.lovable_prompt_result) return;  // guardia extra sul raw content
    if (!topHypothesis) return;
    autoRunRef.current = true;
    generateLovablePrompt();
  }, [topHypothesis]);



  const copyPrompt = async () => {
    const text =
      lovableResult?.base_prompt ||
      content.base_prompt ||
      (content.lovable_prompt_result as LovablePromptResult)?.base_prompt;
    if (!text) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for non-secure contexts
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
      toast.success("Prompt copiato negli appunti");
    } catch {
      toast.error("Copia non riuscita. Seleziona il testo manualmente.");
    }
  };

  const checklistItems: Array<{ key: keyof LovablePromptResult["quality_checklist"]; label: string }> = [
    { key: "target_clarity", label: "Target" },
    { key: "real_problem", label: "Problema" },
    { key: "differentiation", label: "Differenz." },
    { key: "decision_criteria", label: "Criteri" },
    { key: "actionable_output", label: "Output" },
  ];

  return (
    <div className="space-y-5">
      <StepIntroCard text="Un MVP (Minimum Viable Product) è la versione più semplice del tuo prodotto che permette di testare l'ipotesi principale — niente funzionalità in più, niente di meno. Il modo più veloce per costruirlo non è codice: è Lovable. In questo step definisci i criteri di successo e generi il prompt per costruire il tuo MVP. Hai circa 15 crediti gratuiti — bastano per un mini-prodotto funzionante." />
      {/* 1. Intestazione */}
      <div className="space-y-1">
        <p className="text-[11px] uppercase tracking-wider text-secondary font-medium">
          Step 2 di 5 · Costruisci il tuo MVP
        </p>
        <p className="text-sm text-slate-300">
          Il tuo Base Prompt Lovable è pronto. Copialo e costruisci in 15 minuti.
        </p>
      </div>

      {/* 2. Ipotesi in corso (read-only) */}
      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] uppercase tracking-wider text-slate-300 font-medium">
            Ipotesi in test
          </p>
          {onGoToStep && (
            <button
              type="button"
              onClick={() => onGoToStep(1)}
              className="text-xs text-secondary hover:text-secondary/80 font-medium shrink-0"
            >
              Modifica → Step 1
            </button>
          )}
        </div>
        {topHypothesis ? (
          <p className="text-sm text-slate-100 leading-snug">{topHypothesis}</p>
        ) : (
          <p className="text-sm text-slate-300 italic">
            Nessuna ipotesi definita. Torna allo Step 1 per scriverla.
          </p>
        )}
      </div>

      {/* 3. Box Base Prompt */}
      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-1.5">
        <p className="text-xs uppercase tracking-wider text-secondary font-medium">Perché costruiamo prima con Lovable?</p>
        <p className="text-sm text-slate-300 leading-relaxed">
          Invece di spendere settimane a sviluppare, costruiamo un prototipo reale in 15 minuti. Lo usiamo per portare persone vere davanti alla tua idea e misurare una reazione concreta — non un'opinione.
        </p>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-800/60 overflow-hidden">

        <div className="px-4 py-3 border-b border-slate-700/60 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-slate-100">✦ Base Prompt Lovable</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyPrompt}
              disabled={!lovableResult || lovableLoading}
              className="text-xs px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors disabled:opacity-50"
            >
              {promptCopied ? "Copiato!" : "Copia prompt"}
            </button>
            <button
              type="button"
              onClick={() => {
                if (lovableResult || content.lovable_prompt_result) {
                  if (!window.confirm("Sei sicuro di voler rigenerare il prompt? Il prompt attuale verrà sovrascritto.")) return;
                }
                generateLovablePrompt();
              }}
              disabled={lovableLoading || disabled}
              className="text-xs px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-200 transition-colors disabled:opacity-50"
            >
              {lovableLoading ? "…" : "Rigenera prompt ↻"}
            </button>

          </div>
        </div>

        <div className="p-4">
          {lovableLoading && !lovableResult && (
            <div className="flex items-center gap-2 text-sm text-slate-300 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Sto generando il tuo Base Prompt Lovable…
            </div>
          )}
          {lovableError && !lovableLoading && (
            <p className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
              {lovableError}
            </p>
          )}
          {lovableResult && (
            <pre className="text-xs text-slate-200 whitespace-pre-wrap leading-relaxed bg-slate-900/60 rounded-lg p-3 max-h-64 overflow-y-auto font-sans">
              {lovableResult.base_prompt}
            </pre>
          )}
        </div>

        {/* 4. Quality checklist compatta */}
        {lovableResult && (
          <div className="px-4 pb-4">
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
              {checklistItems.map(({ key, label }) => {
                const item = lovableResult.quality_checklist[key] as QualityCheckItem;
                const pass = !!item?.pass;
                return (
                  <span key={key as string} className="flex items-center gap-1">
                    <span className={pass ? "text-emerald-400" : "text-amber-400"}>
                      {pass ? "✓" : "△"}
                    </span>
                    <span className="text-slate-300">{label}</span>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* 5. Come misurerai il test */}
      <div className="space-y-3">
        <p className="text-[11px] uppercase tracking-wider text-secondary font-medium">
          Come misurerai il test
        </p>
        <Field label="Metrica" hint="Cosa conterai concretamente.">
          <Textarea
            rows={2}
            disabled={disabled}
            className={inputClass}
            value={content.suggested_metric ?? ""}
            onChange={(e) => set("suggested_metric", e.target.value)}
            placeholder={lovableLoading ? "Generazione…" : "Es. numero di utenti che tornano dopo 48h"}
          />
        </Field>
        <Field label="Soglia minima" hint="Numero + tempo. Es. 'Almeno 3 utenti attivi entro 7 giorni.'">
          <Textarea
            rows={2}
            disabled={disabled}
            className={inputClass}
            value={content.suggested_threshold ?? ""}
            onChange={(e) => {
              set("suggested_threshold", e.target.value);
              set("success_threshold", e.target.value);
            }}
            placeholder={lovableLoading ? "Generazione…" : "Es. ≥3 utenti attivi in 7 giorni"}
          />
        </Field>
        <Field label="Criterio di fallimento" hint="Cosa renderebbe il test chiaramente negativo.">
          <Textarea
            rows={2}
            disabled={disabled}
            className={inputClass}
            value={content.suggested_failure_criteria ?? ""}
            onChange={(e) => set("suggested_failure_criteria", e.target.value)}
            placeholder={lovableLoading ? "Generazione…" : "Es. meno di 2 utenti tornano entro 7 giorni"}
          />
        </Field>

      </div>

      {/* 6. Come costruire il tuo MVP in 15 minuti */}
      <div className="rounded-xl border border-primary/30 bg-primary/[0.06] p-5 space-y-4">
        <h3 className="text-sm font-semibold text-slate-100">
          Come costruire il tuo MVP in 15 minuti
        </h3>
        <ol className="space-y-2 text-sm text-slate-200">
          <li><span className="text-primary font-semibold mr-2">①</span>Copia il prompt qui sopra</li>
          <li><span className="text-primary font-semibold mr-2">②</span>Clicca "Apri Lovable" — ottieni 10 crediti bonus gratuiti al primo accesso</li>
          <li><span className="text-primary font-semibold mr-2">③</span>Incolla il prompt nella chat di Lovable e premi invio</li>
          <li><span className="text-primary font-semibold mr-2">④</span>Aspetta ~3 minuti che costruisca la tua app</li>
          <li><span className="text-primary font-semibold mr-2">⑤</span>Condividi il link della tua app con almeno 10 persone</li>
          <li><span className="text-primary font-semibold mr-2">⑥</span>Torna qui e clicca "Ho costruito il mio MVP →"</li>
        </ol>
        <a
          href={LOVABLE_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg bg-primary hover:bg-primary/90 text-white text-base font-semibold transition-colors"
        >
          🚀 Apri Lovable
        </a>
      </div>
    </div>
  );
}


// ---------- Step 3 ----------
type TrackableItem = { id: string; label: string; checked: boolean };

function normalizeList(raw: any): TrackableItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((it: any) => {
      if (!it || typeof it !== "object") return null;
      const id = typeof it.id === "string" && it.id ? it.id : (typeof crypto !== "undefined" && "randomUUID" in crypto ? crypto.randomUUID() : `legacy_${Math.random().toString(36).slice(2)}`);
      const label = typeof it.label === "string" ? it.label : "";
      const checked = !!it.checked;
      return { id, label, checked };
    })
    .filter(Boolean) as TrackableItem[];
}

// ---- Step 3 shared utilities --------------------------------------------

const NEXT_ACTION_TAG: Record<NextAction["category"], { label: string; cls: string }> = {
  prep: { label: "Prep", cls: "border-secondary/40 bg-secondary/10 text-secondary" },
  live: { label: "Live", cls: "border-primary/40 bg-primary/10 text-primary" },
  measure: { label: "Misura", cls: "border-amber-400/40 bg-amber-500/10 text-amber-200" },
  evidence: { label: "Prova", cls: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200" },
};

function buildSnapshotLike(
  step1: Record<string, any> | undefined,
  step2: Record<string, any> | undefined,
): ExperimentSnapshotLike {
  const explicitType = step2?.selected_experiment;
  const hasLovablePrompt = !!(step2?.lovable_prompt_result ?? step2?.base_prompt);
  const norm = normalizeExperimentId(explicitType ?? (hasLovablePrompt ? "landing_waitlist" : undefined));
  const presetLabel = getExperimentPreset(step2?.selected_experiment)?.label;
  return {
    top_hypothesis: step1?.top_hypothesis ?? "",
    experiment_type: norm,
    experiment_label: presetLabel ?? step2?.selected_experiment ?? "",
    target: step2?.target ?? "",
    channel: step2?.channel ?? "",
    action: step2?.action ?? "",
    primary_metric: step2?.primary_metric ?? "",
    success_threshold: step2?.success_threshold ?? "",
    duration_days: step2?.duration_days ?? null,
    minimum_sample: step2?.minimum_sample ?? null,
  };
}

function Step3({
  content,
  set,
  disabled,
  saving,
  readOnly,
  isStepCompleted,
  allSubmissions,
  saveDraft,
  completeStep,
  onGoToStep,
  realityCheck,
}: {
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  disabled: boolean;
  saving: boolean;
  readOnly: boolean;
  isStepCompleted: boolean;
  allSubmissions: V2SprintSubmission[];
  saveDraft: (patch: Record<string, any>, opts?: { unlockCall?: boolean }) => Promise<void>;
  completeStep: (patch: Record<string, any>) => Promise<void>;
  onGoToStep?: (step: number) => void;
  realityCheck: V2RealityCheck | null;
}) {
  const step1 = allSubmissions.find((s) => s.step_number === 1)?.content as
    | Record<string, any>
    | undefined;
  const step2 = allSubmissions.find((s) => s.step_number === 2)?.content as
    | Record<string, any>
    | undefined;
  const topHypothesis: string = step1?.top_hypothesis?.trim?.() ?? "";

  const liveExperiment: string =
    step2?.selected_experiment ??
    ((step2?.lovable_prompt_result || step2?.base_prompt) ? "landing_waitlist" : undefined) ??
    realityCheck?.test_channel ??
    "";
  const step2Ready =
    !!step2 && (str(step2.suggested_threshold) || str(step2.success_threshold));

  const isLaunched = !!content.test_launched_at;

  const normalizedExperimentId = normalizeExperimentId(
    isLaunched
      ? content.experiment_snapshot?.experiment_type ?? liveExperiment
      : liveExperiment,
  );

  const metricsList = normalizeList(content.metrics_to_collect);
  const evidenceList = normalizeList(content.evidence_to_collect);

  // Seed metrics/evidence once before launch. After launch the lists are frozen.
  const seedRef = useRef<string | null>(null);
  useEffect(() => {
    if (readOnly || isStepCompleted) return;
    if (!step2Ready) return;
    if (isLaunched) return;

    if (seedRef.current === normalizedExperimentId) return;
    seedRef.current = normalizedExperimentId;

    if (
      content.metrics_to_collect === undefined ||
      (Array.isArray(content.metrics_to_collect) &&
        content.metrics_to_collect.length === 0 &&
        normalizedExperimentId !== "custom")
    ) {
      const next =
        normalizedExperimentId === "custom"
          ? []
          : buildMetricsToCollect(normalizedExperimentId).map((s) => ({
              id: s.id,
              label: s.label,
              checked: false,
            }));
      set("metrics_to_collect", next);
    }

    if (
      content.evidence_to_collect === undefined ||
      (Array.isArray(content.evidence_to_collect) &&
        content.evidence_to_collect.length === 0 &&
        normalizedExperimentId !== "custom")
    ) {
      const next =
        normalizedExperimentId === "custom"
          ? []
          : buildEvidenceToCollect(normalizedExperimentId).map((s) => ({
              id: s.id,
              label: s.label,
              checked: false,
            }));
      set("evidence_to_collect", next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedExperimentId, step2Ready, readOnly, isStepCompleted, isLaunched]);

  if (!step2Ready && !isLaunched) {
    return (
      <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-5 space-y-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-amber-300 mt-0.5 shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-semibold text-slate-100">
              Step 2 ancora da completare
            </p>
            <p className="text-sm text-slate-300">
              Per preparare il protocollo devi prima completare lo Step 2.
              Lì definiamo target, canale, metrica e soglia minima.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isLaunched) {
    return (
      <Step3Preparation
        content={content}
        set={set}
        disabled={disabled}
        readOnly={readOnly}
        topHypothesis={topHypothesis}
        step2={step2}
        realityCheck={realityCheck}
        metricsList={metricsList}
        evidenceList={evidenceList}
        saveDraft={saveDraft}
      />
    );
  }

  return (
    <Step3Execution
      content={content}
      set={set}
      disabled={disabled}
      saving={saving}
      readOnly={readOnly}
      isStepCompleted={isStepCompleted}
      metricsList={metricsList}
      evidenceList={evidenceList}
      step2={step2}
      realityCheck={realityCheck}
      topHypothesis={topHypothesis}
      saveDraft={saveDraft}
      completeStep={completeStep}
      onGoToStep={onGoToStep}
    />
  );
}

// ---- "Cosa fare adesso" queue (shared between preparation + execution) ----
function NextActionsQueue({
  actions,
  onClickAction,
  compact,
  emptyMessage,
}: {
  actions: NextAction[];
  onClickAction: (a: NextAction) => void;
  compact?: boolean;
  emptyMessage?: string;
}) {
  if (actions.length === 0) {
    if (!emptyMessage) return null;
    return (
      <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/5 p-4">
        <p className="text-sm text-emerald-100 leading-relaxed">{emptyMessage}</p>
      </div>
    );
  }
  return (
    <div
      className={cn(
        "rounded-xl border border-primary/30 bg-primary/5 backdrop-blur-sm",
        compact ? "p-3 space-y-2" : "p-4 space-y-3",
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-[11px] uppercase tracking-wider text-primary font-medium">
          Cosa fare adesso
        </p>
        {!compact && (
          <p className="text-[11px] text-slate-300">
            {actions.length === 1 ? "1 azione" : `${actions.length} azioni`}
          </p>
        )}
      </div>
      {!compact && (
        <p className="text-xs text-slate-300 leading-snug">
          Completa queste azioni nell'ordine indicato per portare il test verso una
          prova reale.
        </p>
      )}
      <ul className="space-y-1.5">
        {actions.map((a) => {
          const tag = NEXT_ACTION_TAG[a.category];
          return (
            <li key={a.id}>
              <button
                type="button"
                onClick={() => onClickAction(a)}
                className="group w-full text-left rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] hover:border-white/20 px-3 py-2 transition-colors flex items-start gap-2.5"
              >
                <span
                  className={cn(
                    "shrink-0 mt-0.5 inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-medium",
                    tag.cls,
                  )}
                >
                  {tag.label}
                </span>
                <span className="flex-1 text-sm text-slate-100 leading-snug">
                  {a.label}
                </span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-300 mt-1 shrink-0" />
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function useHighlight() {
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);
  const flash = (id: string) => {
    if (timerRef.current) window.clearTimeout(timerRef.current);
    setHighlightedId(id);
    timerRef.current = window.setTimeout(() => setHighlightedId(null), 1200);
  };
  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    [],
  );
  return { highlightedId, flash };
}

// ---- Shared traffic-copy UI for Step 3 ----

type TrafficCopyResult = {
  dm_message: {
    body: string;
    channel_hint: string;
    finding_hint?: string;
  };
  community_post: {
    body: string;
    suggested_communities: string[];
  };
  quick_wins: string[];
};

function TrafficCopyPanel({
  content,
  set,
  disabled,
  realityCheck,
  step2,
  topHypothesis,
  readOnlyUrl = false,
  inAccordion = false,
}: {
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  disabled: boolean;
  realityCheck: V2RealityCheck | null;
  step2: Record<string, any> | undefined;
  topHypothesis: string;
  readOnlyUrl?: boolean;
  inAccordion?: boolean;
}) {
  const [trafficResult, setTrafficResult] = useState<TrafficCopyResult | null>(
    (content.traffic_copy_result as TrafficCopyResult) ?? null,
  );
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [trafficError, setTrafficError] = useState<string | null>(null);
  const [dmCopied, setDmCopied] = useState(false);
  const [postCopied, setPostCopied] = useState(false);

  const [followupResult, setFollowupResult] = useState<{ body: string; channel_hint: string } | null>(
    (content.followup_copy as { body: string; channel_hint: string }) ?? null,
  );
  const [followupLoading, setFollowupLoading] = useState(false);
  const [followupError, setFollowupError] = useState<string | null>(null);
  const [followupCopied, setFollowupCopied] = useState(false);

  const [questionsResult, setQuestionsResult] = useState<{ questions: string[] } | null>(
    (content.questions_copy as { questions: string[] }) ?? null,
  );
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  const [questionsCopied, setQuestionsCopied] = useState(false);

  const generateFollowup = async () => {
    setFollowupLoading(true);
    setFollowupError(null);
    try {
      const { data, error } = await supabase.functions.invoke("v2-sprint-traffic-copy", {
        body: {
          type: "followup",
          hypothesis: topHypothesis ?? "",
          target: realityCheck?.target_summary ?? step2?.target ?? "",
          rc_test_channel: realityCheck?.test_channel ?? step2?.channel ?? "",
        },
      });
      if (error || !data) throw new Error("generation_failed");
      setFollowupResult(data as { body: string; channel_hint: string });
      set("followup_copy", data);
    } catch {
      setFollowupError("Non è stato possibile generare il copy. Riprova.");
    } finally {
      setFollowupLoading(false);
    }
  };

  const generateQuestions = async () => {
    setQuestionsLoading(true);
    setQuestionsError(null);
    try {
      const { data, error } = await supabase.functions.invoke("v2-sprint-traffic-copy", {
        body: {
          type: "questions",
          hypothesis: topHypothesis ?? "",
          target: realityCheck?.target_summary ?? step2?.target ?? "",
        },
      });
      if (error || !data) throw new Error("generation_failed");
      setQuestionsResult(data as { questions: string[] });
      set("questions_copy", data);
    } catch {
      setQuestionsError("Non è stato possibile generare le domande. Riprova.");
    } finally {
      setQuestionsLoading(false);
    }
  };

  const generateTrafficCopy = async () => {
    setTrafficLoading(true);
    setTrafficError(null);
    try {
      const { data, error } = await supabase.functions.invoke(
        "v2-sprint-traffic-copy",
        {
          body: {
            hypothesis: topHypothesis ?? "",
            target: realityCheck?.target_summary ?? step2?.target ?? "",
            channel: realityCheck?.test_channel ?? step2?.channel ?? "linkedin",
            action: step2?.action ?? realityCheck?.experiment_48h ?? "azione misurata nel test",
            success_threshold:
              step2?.suggested_threshold ?? step2?.success_threshold ?? "",
            landing_url: (content.lovable_app_url ?? content.landing_url ?? "") || "",
          },
        },
      );
      if (error || !data) throw new Error("generation_failed");
      setTrafficResult(data as TrafficCopyResult);
      set("traffic_copy_result", data);
    } catch {
      setTrafficError("Non è stato possibile generare il copy. Riprova.");
    } finally {
      setTrafficLoading(false);
    }
  };

  const makeCopier = (text: string, setter: (v: boolean) => void) => async () => {
    await navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const url = content.lovable_app_url ?? content.landing_url ?? "";

  const urlBlock = readOnlyUrl ? (
    <div className="space-y-1">
      <label className="text-sm font-medium text-slate-300">
        Link della tua app Lovable
      </label>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-secondary hover:underline break-all"
      >
        {url || "—"}
      </a>
    </div>
  ) : (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-slate-300">
        Link della tua app Lovable
      </label>
      <input
        type="url"
        value={url}
        onChange={(e) => set("lovable_app_url", e.target.value)}
        placeholder="https://..."
        disabled={disabled}
        className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-50"
      />
      <p className="text-xs text-slate-300">
        Incolla il link dell'app che hai costruito su Lovable.
      </p>
    </div>
  );

  const generateBlock = (
    <div className="space-y-3">
      {!disabled && !trafficLoading && (
        <button
          type="button"
          onClick={generateTrafficCopy}
          className="w-full py-2.5 px-4 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors"
        >
          {trafficResult
            ? "Rigenera copy traffico"
            : "Genera copy per portare traffico"}
        </button>
      )}
      {trafficLoading && (
        <p className="text-center text-sm text-slate-300 py-2">
          Generazione in corso…
        </p>
      )}
      {trafficError && (
        <p className="text-sm text-red-400">{trafficError}</p>
      )}
    </div>
  );

  const resultsBlock = trafficResult && (
    <div className="space-y-4">
      <div className="rounded-xl border border-violet-800/50 bg-violet-900/20 p-4 space-y-2">
        <h4 className="text-sm font-semibold text-violet-300">
          ⚡ Fai queste 3 cose oggi
        </h4>
        <ol className="space-y-1.5">
          {trafficResult.quick_wins.map((win, i) => (
            <li key={i} className="flex gap-2 text-sm text-slate-300">
              <span className="text-violet-400 font-semibold shrink-0">
                {i + 1}.
              </span>
              <span>{win}</span>
            </li>
          ))}
        </ol>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-slate-200">DM diretto</h4>
              <p className="text-xs text-slate-300">
                {trafficResult.dm_message.channel_hint}
              </p>
            </div>
            <button
              type="button"
              onClick={makeCopier(trafficResult.dm_message.body, setDmCopied)}
              className="text-xs px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors shrink-0"
            >
              {dmCopied ? "Copiato ✓" : "Copia"}
            </button>
          </div>
          <pre className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed bg-slate-900/60 rounded-lg p-3 font-sans">
            {trafficResult.dm_message.body}
          </pre>
          {trafficResult.dm_message.finding_hint && (
            <p className="text-xs text-slate-300">
              📍 Dove trovarli: {trafficResult.dm_message.finding_hint}
            </p>
          )}
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-slate-200">Post community</h4>
              <p className="text-xs text-slate-300">
                {trafficResult.community_post.suggested_communities.join(" · ")}
              </p>
            </div>
            <button
              type="button"
              onClick={makeCopier(trafficResult.community_post.body, setPostCopied)}
              className="text-xs px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors shrink-0"
            >
              {postCopied ? "Copiato ✓" : "Copia"}
            </button>
          </div>
          <pre className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed bg-slate-900/60 rounded-lg p-3 font-sans">
            {trafficResult.community_post.body}
          </pre>
        </div>
      </div>
    </div>
  );

  const makeSectionCopier = (text: string, setter: (v: boolean) => void) => async () => {
    await navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 2000);
  };

  const followupBlock = (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-medium text-slate-200">Follow-up DM</h4>
          <p className="text-xs text-slate-300">
            Per chi non ha risposto al DM iniziale dopo 2-3 giorni.
          </p>
        </div>
        {followupResult && !disabled && (
          <button
            type="button"
            onClick={makeSectionCopier(followupResult.body, setFollowupCopied)}
            className="text-xs px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors shrink-0"
          >
            {followupCopied ? "Copiato ✓" : "Copia"}
          </button>
        )}
      </div>
      {followupResult ? (
        <>
          <pre className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed bg-slate-900/60 rounded-lg p-3 font-sans">
            {followupResult.body}
          </pre>
          {!disabled && (
            <button
              type="button"
              onClick={generateFollowup}
              disabled={followupLoading}
              className="text-xs text-slate-300 hover:text-slate-200 disabled:opacity-50"
            >
              {followupLoading ? "Generazione…" : "Rigenera follow-up"}
            </button>
          )}
        </>
      ) : (
        !disabled && (
          <button
            type="button"
            onClick={generateFollowup}
            disabled={followupLoading}
            className="w-full py-2 px-4 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            {followupLoading ? "Generazione in corso…" : "Genera follow-up DM"}
          </button>
        )
      )}
      {followupError && <p className="text-sm text-red-400">{followupError}</p>}
    </div>
  );

  const questionsBlock = (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h4 className="text-sm font-medium text-slate-200">3 Domande</h4>
          <p className="text-xs text-slate-300">
            Da fare nelle conversazioni attive per validare l'ipotesi.
          </p>
        </div>
        {questionsResult && !disabled && (
          <button
            type="button"
            onClick={makeSectionCopier(
              questionsResult.questions.map((q, i) => `${i + 1}. ${q}`).join("\n"),
              setQuestionsCopied,
            )}
            className="text-xs px-3 py-1 rounded-md bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors shrink-0"
          >
            {questionsCopied ? "Copiato ✓" : "Copia"}
          </button>
        )}
      </div>
      {questionsResult ? (
        <>
          <ol className="space-y-1.5">
            {questionsResult.questions.map((q, i) => (
              <li key={i} className="flex gap-2 text-sm text-slate-300">
                <span className="text-violet-400 font-semibold shrink-0">{i + 1}.</span>
                <span>{q}</span>
              </li>
            ))}
          </ol>
          {!disabled && (
            <button
              type="button"
              onClick={generateQuestions}
              disabled={questionsLoading}
              className="text-xs text-slate-300 hover:text-slate-200 disabled:opacity-50"
            >
              {questionsLoading ? "Generazione…" : "Rigenera domande"}
            </button>
          )}
        </>
      ) : (
        !disabled && (
          <button
            type="button"
            onClick={generateQuestions}
            disabled={questionsLoading}
            className="w-full py-2 px-4 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors disabled:opacity-50"
          >
            {questionsLoading ? "Generazione in corso…" : "Genera 3 domande"}
          </button>
        )
      )}
      {questionsError && <p className="text-sm text-red-400">{questionsError}</p>}
    </div>
  );

  const core = (
    <div className="space-y-6">
      {generateBlock}
      {resultsBlock}
      <div className="grid gap-4 md:grid-cols-2">
        {followupBlock}
        {questionsBlock}
      </div>
    </div>
  );

  if (inAccordion) {
    return (
      <div className="space-y-4">
        {urlBlock}
        <Collapsible>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 text-sm text-slate-300 hover:text-slate-100 transition-colors"
            >
              <ChevronDown className="h-4 w-4" />
              <span className="font-medium">Strumenti copy</span>
              <span className="text-xs text-slate-300">DM · Community · Follow-up · 3 Domande</span>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-4 space-y-6">
            {core}
          </CollapsibleContent>
        </Collapsible>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {urlBlock}
      {core}
    </div>
  );
}

// -------- Preparation view --------

function Step3Preparation({
  content,
  set,
  disabled,
  readOnly,
  topHypothesis,
  step2,
  realityCheck,
  metricsList,
  evidenceList,
  saveDraft,
}: {
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  disabled: boolean;
  readOnly: boolean;
  topHypothesis: string;
  step2: Record<string, any> | undefined;
  realityCheck: V2RealityCheck | null;
  metricsList: TrackableItem[];
  evidenceList: TrackableItem[];
  saveDraft: (patch: Record<string, any>, opts?: { unlockCall?: boolean }) => Promise<void>;
}) {
  const [launching, setLaunching] = useState(false);
  const busy = disabled || launching || readOnly;

  const duration = step2?.duration_days
    ? `${step2.duration_days} giorni`
    : "qualche giorno";
  const expLabel =
    getExperimentPreset(step2?.selected_experiment)?.label ??
    step2?.selected_experiment ??
    "—";

  const handleLaunch = async () => {
    if (busy) return;
    setLaunching(true);
    try {
      const now = new Date().toISOString();
      const snapshotObj = buildSnapshotLike(
        { top_hypothesis: topHypothesis } as any,
        step2,
      );
      set("test_launched_at", now);
      await saveDraft(
        {
          test_launched_at: now,
          execution_status: "running",
          execution_started_at: now,
          execution_last_saved_at: now,
          experiment_snapshot: snapshotObj,
          metrics_to_collect: metricsList,
          evidence_to_collect: evidenceList,
          protocol_notes: content.protocol_notes ?? "",
          time_budget: content.time_budget ?? "",
        },
      );

      toast.success("Test lanciato. Inizia a raccogliere dati reali.");
    } catch (e: any) {
      toast.error(e?.message ?? "Errore nel lancio del test.");
      set("test_launched_at", content.test_launched_at);
    } finally {
      setLaunching(false);
    }
  };

  return (
    <div className="space-y-6">
      <StepIntroCard text="Non amici, non parenti — persone con il problema che hai ipotizzato. Il tuo obiettivo è raggiungere la soglia che hai definito nello Step 2. Hai già il copy pronto per DM, post community e follow-up. Il tuo unico compito adesso è distribuirlo." />
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/10 px-2.5 py-1 text-[11px] uppercase tracking-wider text-secondary font-medium">
          <PlayCircle className="h-3.5 w-3.5" />
          Prepara il lancio
        </span>
      </div>

      <div className="rounded-xl border border-secondary/30 bg-secondary/5 p-4 space-y-2">
        <p className="text-xs uppercase tracking-wider text-secondary font-medium">
          Riepilogo del test
        </p>
        <p className="text-sm text-slate-200 leading-relaxed">
          Nei prossimi <strong className="text-slate-100">{duration}</strong>{" "}
          testerai l'ipotesi{" "}
          <strong className="text-slate-100">«{topHypothesis}»</strong> con un{" "}
          <strong className="text-slate-100">{expLabel}</strong>, raggiungendo{" "}
          <strong className="text-slate-100">{step2?.target}</strong> tramite{" "}
          <strong className="text-slate-100">{step2?.channel}</strong>. Azione
          misurata:{" "}
          <strong className="text-slate-100">{step2?.action}</strong>. Il test
          è positivo se{" "}
          <strong className="text-slate-100">{step2?.success_threshold}</strong>
          {step2?.primary_metric ? ` (${step2.primary_metric})` : ""}.
        </p>
      </div>

      <TrafficCopyPanel
        content={content}
        set={set}
        disabled={disabled}
        realityCheck={realityCheck}
        step2={step2}
        topHypothesis={topHypothesis}
      />

      <Field
        label="Note operative (opzionale)"
        hint="Annotazioni utili prima di lanciare il test: messaggi da preparare, persone da contattare, vincoli o rischi operativi."
      >
        <Textarea
          rows={3}
          disabled={disabled}
          className={inputClass}
          value={content.protocol_notes ?? ""}
          onChange={(e) => set("protocol_notes", e.target.value)}
          placeholder="Es. Pubblica giovedì alle 18, gestisci DM entro 24h."
        />
      </Field>

      <Collapsible>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 text-sm text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ChevronDown className="h-4 w-4" />
            Dettagli operativi opzionali
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3 space-y-4 border-l border-white/10 pl-4">
          <Field
            label="Tempo / budget (opzionale)"
            hint="Se vuoi annotare un budget operativo distinto da quello già definito nello Step 2."
          >
            <Input
              disabled={disabled}
              className={inputClass}
              value={content.time_budget ?? ""}
              onChange={(e) => set("time_budget", e.target.value)}
              placeholder="Es. 3 giorni, 50€ ads"
            />
          </Field>
        </CollapsibleContent>
      </Collapsible>

      <Button
        type="button"
        disabled={busy}
        onClick={handleLaunch}
        className="w-full bg-primary hover:bg-primary/90"
      >
        {launching ? (
          <>
            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
            Lancio in corso…
          </>
        ) : (
          <>
            <PlayCircle className="h-4 w-4 mr-1.5" />
            Lancia il test
          </>
        )}
      </Button>
    </div>
  );
}

// -------- Threshold progress + Daily log --------

type DailyLog = {
  date: string;
  contatti: number;
  risposte: number;
  conversioni: number;
};

function parseThresholdSimple(raw: unknown): { value: number; label: string } | null {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const match = s.match(/(\d+(?:[.,]\d+)?)/);
  if (!match) return null;
  const value = parseFloat(match[1].replace(",", "."));
  if (!Number.isFinite(value) || value <= 0) return null;
  const label = s.replace(match[0], "").trim() || "conversioni";
  return { value, label };
}

function ThresholdProgress({
  content,
  step2,
}: {
  content: Record<string, any>;
  step2: Record<string, any> | undefined;
}) {
  const raw =
    step2?.suggested_threshold ?? step2?.success_threshold ?? content.experiment_snapshot?.success_threshold;
  const parsed = parseThresholdSimple(raw);
  if (!parsed) return null;

  const logs: DailyLog[] = Array.isArray(content.daily_logs) ? content.daily_logs : [];
  const total = logs.reduce((sum, l) => sum + (Number(l?.conversioni) || 0), 0);
  const percent = Math.min(100, Math.round((total / parsed.value) * 100));

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-200 font-medium">
          {total} / {parsed.value} {parsed.label}
        </span>
        <span className="text-xs text-slate-300">{percent}%</span>
      </div>
      <Progress value={percent} className="h-2" />
    </div>
  );
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDayShort(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("it-IT", { day: "numeric", month: "short" }).replace(".", "");
}

function DailyLogPanel({
  content,
  set,
  saveDraft,
  disabled,
  saving,
}: {
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  saveDraft: (patch: Record<string, any>, opts?: { unlockCall?: boolean }) => Promise<void>;
  disabled: boolean;
  saving: boolean;
}) {
  const logs: DailyLog[] = useMemo(
    () => (Array.isArray(content.daily_logs) ? content.daily_logs : []),
    [content.daily_logs],
  );
  const today = todayIso();
  const existing = logs.find((l) => l.date === today);

  const [contatti, setContatti] = useState<string>(
    existing ? String(existing.contatti ?? "") : "",
  );
  const [risposte, setRisposte] = useState<string>(
    existing ? String(existing.risposte ?? "") : "",
  );
  const [conversioni, setConversioni] = useState<string>(
    existing ? String(existing.conversioni ?? "") : "",
  );
  const [savingLog, setSavingLog] = useState(false);

  useEffect(() => {
    setContatti(existing ? String(existing.contatti ?? "") : "");
    setRisposte(existing ? String(existing.risposte ?? "") : "");
    setConversioni(existing ? String(existing.conversioni ?? "") : "");
  }, [existing?.date, existing?.contatti, existing?.risposte, existing?.conversioni]);

  const handleSave = async () => {
    if (disabled || savingLog) return;
    setSavingLog(true);
    try {
      const entry: DailyLog = {
        date: today,
        contatti: Number(contatti) || 0,
        risposte: Number(risposte) || 0,
        conversioni: Number(conversioni) || 0,
      };
      const others = logs.filter((l) => l.date !== today);
      const next = [...others, entry].sort((a, b) => (a.date < b.date ? -1 : 1));
      set("daily_logs", next);
      await saveDraft({ daily_logs: next });
      toast.success("Log di oggi salvato.");
    } finally {
      setSavingLog(false);
    }
  };

  const lastFive = [...logs].sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 5).reverse();

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
      <div>
        <p className="text-[11px] uppercase tracking-wider text-secondary font-medium">
          Log giornaliero
        </p>
        <p className="text-xs text-slate-300">
          Registra ogni giorno contatti raggiunti, risposte ricevute e conversioni.
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1 text-xs text-slate-300">
          <span>Contatti raggiunti</span>
          <input
            type="number"
            min="0"
            value={contatti}
            onChange={(e) => setContatti(e.target.value)}
            disabled={disabled || savingLog}
            className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-50"
          />
        </label>
        <label className="space-y-1 text-xs text-slate-300">
          <span>Risposte ricevute</span>
          <input
            type="number"
            min="0"
            value={risposte}
            onChange={(e) => setRisposte(e.target.value)}
            disabled={disabled || savingLog}
            className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-50"
          />
        </label>
        <label className="space-y-1 text-xs text-slate-300">
          <span>Conversioni (verso soglia)</span>
          <input
            type="number"
            min="0"
            value={conversioni}
            onChange={(e) => setConversioni(e.target.value)}
            disabled={disabled || savingLog}
            className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-500 disabled:opacity-50"
          />
        </label>
      </div>
      <button
        type="button"
        onClick={handleSave}
        disabled={disabled || savingLog || saving}
        className="text-xs px-3 py-1.5 rounded-md bg-violet-600 hover:bg-violet-500 text-white transition-colors disabled:opacity-50"
      >
        {savingLog ? "Salvataggio…" : "Salva log di oggi"}
      </button>
      {lastFive.length > 0 && (
        <p className="text-xs text-slate-300">
          {lastFive
            .map((l) => `${formatDayShort(l.date)}: ${l.contatti}/${l.risposte}/${l.conversioni}`)
            .join(" · ")}
        </p>
      )}
    </div>
  );
}

// -------- Execution view --------


function Step3Execution({
  content,
  set,
  disabled,
  saving,
  readOnly,
  isStepCompleted,
  metricsList,
  evidenceList,
  step2,
  realityCheck,
  topHypothesis,
  saveDraft,
  completeStep,
  onGoToStep,
}: {
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  disabled: boolean;
  saving: boolean;
  readOnly: boolean;
  isStepCompleted: boolean;
  metricsList: TrackableItem[];
  evidenceList: TrackableItem[];
  step2: Record<string, any> | undefined;
  realityCheck: V2RealityCheck | null;
  topHypothesis: string;
  saveDraft: (patch: Record<string, any>, opts?: { unlockCall?: boolean }) => Promise<void>;
  completeStep: (patch: Record<string, any>) => Promise<void>;
  onGoToStep?: (step: number) => void;
}) {
  const isReadyForResults = content.execution_status === "ready_for_results";

  const [pending, setPending] = useState<"progress" | "handoff" | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [forceContinue, setForceContinue] = useState(false);
  const busy = saving || pending !== null || disabled || readOnly;

  const launchedAt =
    typeof content.test_launched_at === "string"
      ? new Date(content.test_launched_at)
      : null;
  const now = Date.now();
  const dayNumber = launchedAt
    ? Math.min(
        Math.floor(
          (now - launchedAt.getTime()) / (1000 * 60 * 60 * 24),
        ) + 1,
        7,
      )
    : 1;
  const launchedAtFormatted = launchedAt
    ? `${launchedAt.toLocaleDateString("it-IT", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })} alle ${launchedAt.toLocaleTimeString("it-IT", {
        hour: "2-digit",
        minute: "2-digit",
      })}`
    : "—";

  const metricsRef = useRef<HTMLDivElement>(null);
  const evidenceRef = useRef<HTMLDivElement>(null);
  const { highlightedId, flash } = useHighlight();

  const nextActions = useMemo(
    () =>
      getNextActions({
        preparationChecklist: [],
        executionChecklist: [],
        metricsToCollect: metricsList,
        evidenceToCollect: evidenceList,
      }),
    [metricsList, evidenceList],
  );

  const scrollToCategory = (a: NextAction) => {
    const target =
      a.category === "measure"
        ? metricsRef.current
        : a.category === "evidence"
          ? evidenceRef.current
          : null;
    if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
    flash(a.sourceId);
  };

  const handleSaveProgress = async () => {
    if (busy) return;
    setPending("progress");
    try {
      await saveDraft({
        execution_status: "running",
        execution_last_saved_at: new Date().toISOString(),
        metrics_to_collect: metricsList,
        evidence_to_collect: evidenceList,
        protocol_notes: content.protocol_notes ?? "",
      });
      toast.success("Progressi salvati.");
    } finally {
      setPending(null);
    }
  };

  const openHandoff = () => {
    if (busy) return;
    setForceContinue(false);
    setConfirmOpen(true);
  };

  const handleConfirmHandoff = async () => {
    if (busy) return;
    if (noRealData && !forceContinue) {
      setForceContinue(true);
      return;
    }
    setPending("handoff");
    try {
      const now = new Date().toISOString();
      await completeStep({
        execution_status: "ready_for_results",
        results_ready_confirmed_at: now,
        execution_last_saved_at: now,
        metrics_to_collect: metricsList,
        evidence_to_collect: evidenceList,
        protocol_notes: content.protocol_notes ?? "",
      });
      setConfirmOpen(false);
    } finally {
      setPending(null);
    }
  };

  const metricsChecked = metricsList.filter((m) => m.checked).length;
  const evidenceChecked = evidenceList.filter((e) => e.checked).length;
  const noRealData = metricsChecked === 0 && evidenceChecked === 0;

  const setListChecked = (
    key: "metrics_to_collect" | "evidence_to_collect",
    list: TrackableItem[],
    id: string,
  ) => {
    set(
      key,
      list.map((x) => (x.id === id ? { ...x, checked: !x.checked } : x)),
    );
  };

  const url = content.lovable_app_url ?? content.landing_url ?? "";

  return (
    <div className="space-y-5">
      <StepIntroCard text="Non amici, non parenti — persone con il problema che hai ipotizzato. Il tuo obiettivo è raggiungere la soglia che hai definito nello Step 2. Hai già il copy pronto per DM, post community e follow-up. Il tuo unico compito adesso è distribuirlo." />
      {/* Banner */}
      <div className="rounded-xl border border-secondary/30 bg-secondary/5 p-4 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary/50 bg-secondary/15 px-2.5 py-1 text-[11px] uppercase tracking-wider text-secondary font-medium">
            <PlayCircle className="h-3.5 w-3.5" />
            {isReadyForResults ? "Test concluso" : "Test in corso"}
          </span>
          <span className="text-xs text-slate-300">
            Test lanciato il {launchedAtFormatted}
          </span>
        </div>
        <p className="text-sm text-slate-200">Giorno {dayNumber} di 7</p>
      </div>

      {/* Read-only URL */}
      <div className="space-y-1">
        <label className="text-sm font-medium text-slate-300">
          Link della tua app Lovable
        </label>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-secondary hover:underline break-all"
        >
          {url || "—"}
        </a>
      </div>

      {/* Progresso verso la soglia */}
      <ThresholdProgress content={content} step2={step2} />

      {/* Log giornaliero */}
      <DailyLogPanel
        content={content}
        set={set}
        saveDraft={saveDraft}
        disabled={busy}
        saving={saving}
      />

      {/* Traffic copy accordion */}
      <TrafficCopyPanel
        content={content}
        set={set}
        disabled={disabled}
        realityCheck={realityCheck}
        step2={step2}
        topHypothesis={topHypothesis}
        readOnlyUrl
        inAccordion
      />

      {/* Cosa fare adesso */}
      {!isReadyForResults && (
        <NextActionsQueue
          actions={nextActions}
          onClickAction={scrollToCategory}
          emptyMessage="Hai completato le azioni previste. Registra i risultati nello Step 4."
        />
      )}

      {/* Metriche */}
      {metricsList.length > 0 && (
        <div
          ref={metricsRef}
          className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-2"
        >
          <p className="text-[11px] uppercase tracking-wider text-secondary font-medium">
            Metriche da raccogliere
          </p>
          <p className="text-xs text-slate-300">
            Spunta solo quando hai almeno un valore reale da inserire nello Step 4.
          </p>
          <ul className="mt-1 space-y-1">
            {metricsList.map((m) => (
              <li
                key={m.id}
                data-item-id={m.id}
                className={cn(
                  "flex items-center gap-2 text-sm text-slate-200 rounded-md px-1 py-0.5 transition-shadow",
                  highlightedId === m.id && "ring-2 ring-secondary/70",
                )}
              >
                <Checkbox
                  checked={m.checked}
                  disabled={busy}
                  onCheckedChange={() =>
                    setListChecked("metrics_to_collect", metricsList, m.id)
                  }
                />
                <span>{m.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Evidenze */}
      {evidenceList.length > 0 && (
        <div
          ref={evidenceRef}
          className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-2"
        >
          <p className="text-[11px] uppercase tracking-wider text-secondary font-medium">
            Evidenze da salvare
          </p>
          <p className="text-xs text-slate-300">
            Spunta solo quando hai effettivamente raccolto e salvato questa prova.
          </p>
          <ul className="mt-1 space-y-1">
            {evidenceList.map((e) => (
              <li
                key={e.id}
                data-item-id={e.id}
                className={cn(
                  "flex items-center gap-2 text-sm text-slate-200 rounded-md px-1 py-0.5 transition-shadow",
                  highlightedId === e.id && "ring-2 ring-secondary/70",
                )}
              >
                <Checkbox
                  checked={e.checked}
                  disabled={busy}
                  onCheckedChange={() =>
                    setListChecked("evidence_to_collect", evidenceList, e.id)
                  }
                />
                <span>{e.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* CTAs */}
      {!readOnly && !isReadyForResults && (
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={handleSaveProgress}
            className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
          >
            {pending === "progress" ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Salvataggio…
              </>
            ) : (
              "Salva progressi"
            )}
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={openHandoff}
            className="bg-primary hover:bg-primary/90"
          >
            <ArrowRight className="h-4 w-4 mr-1.5" />
            Ho raccolto dati reali
          </Button>
        </div>
      )}

      {isReadyForResults && onGoToStep && (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            disabled={saving || pending !== null}
            onClick={() => {
              if (readOnly) {
                onGoToStep(4);
                return;
              }
              openHandoff();
            }}
            className="bg-primary hover:bg-primary/90"
          >
            {pending === "handoff" ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Salvataggio…
              </>
            ) : (
              "Vai allo Step 4"
            )}
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </div>
      )}

      {/* Handoff dialog */}
      <AlertDialog
        open={confirmOpen}
        onOpenChange={(open) => {
          if (pending === "handoff") return;
          setConfirmOpen(open);
          if (!open) setForceContinue(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">
              Prima di registrare i risultati
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-left">
                <div className="overflow-hidden rounded-lg border border-border bg-muted/40 text-sm text-foreground">
                  <div className="grid grid-cols-[1fr_auto] gap-3 border-b border-border px-3 py-2">
                    <span>Metriche raccolte</span>
                    <strong>{metricsChecked}/{metricsList.length || 0}</strong>
                  </div>
                  <div className="grid grid-cols-[1fr_auto] gap-3 px-3 py-2">
                    <span>Evidenze raccolte</span>
                    <strong>{evidenceChecked}/{evidenceList.length || 0}</strong>
                  </div>
                </div>
                {noRealData && (
                  <div
                    role="alert"
                    className="rounded-md border border-amber-300/70 bg-amber-100 p-3 text-sm font-semibold text-amber-950 dark:border-amber-200/60 dark:bg-amber-300/25 dark:text-amber-50"
                  >
                    Non hai ancora indicato dati o prove raccolte. Lo Step 4 sarà utile solo dopo aver eseguito almeno una parte del test.
                  </div>
                )}
                <p className="text-sm text-foreground">
                  Hai almeno un dato reale, una risposta o una prova concreta da registrare?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={pending === "handoff"}>
              Torna all'esecuzione
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={pending === "handoff"}
              onClick={(e) => {
                e.preventDefault();
                handleConfirmHandoff();
              }}
              className="bg-primary hover:bg-primary/90"
            >
              {pending === "handoff" ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  Salvataggio…
                </>
              ) : noRealData && !forceContinue ? (
                "Continua comunque"
              ) : (
                "Conferma e vai allo Step 4"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------- Step 4 ----------
function Step4({
  content,
  set,
  disabled,
  saving,
  readOnly,
  isStepCompleted,
  allSubmissions,
  links,
  onLinksChange,
  saveDraft,
  completeStep,
}: {
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  disabled: boolean;
  saving: boolean;
  readOnly: boolean;
  isStepCompleted: boolean;
  allSubmissions: V2SprintSubmission[];
  links: string[];
  onLinksChange: (l: string[]) => void;
  saveDraft: (patch: Record<string, any>, opts?: { unlockCall?: boolean }) => Promise<void>;
  completeStep: (patch: Record<string, any>) => Promise<void>;
}) {
  // ---- Snapshot & context ------------------------------------------------
  const step3 = allSubmissions.find((s) => s.step_number === 3)?.content as any;
  const step2 = allSubmissions.find((s) => s.step_number === 2)?.content as any;
  const snapshot: ExperimentSnapshotLike | null =
    step3?.experiment_snapshot ?? null;
  const experimentId =
    snapshot?.experiment_type ??
    step2?.selected_experiment ??
    "";
  const normExpId = normalizeExperimentId(experimentId);
  const experimentLabel =
    snapshot?.experiment_label ??
    getExperimentPreset(experimentId)?.label ??
    "Esperimento custom";
  const thresholdText: string =
    snapshot?.success_threshold ?? step2?.success_threshold ?? "";
  const step2MinimumSample: string | number | null =
    snapshot?.minimum_sample ?? step2?.minimum_sample ?? null;
  const minimumSampleOverride: string | number | null =
    content.minimum_sample_override ?? null;
  const minimumSampleText: string | number | null =
    minimumSampleOverride ?? step2MinimumSample;
  const sampleTarget = parseMinimumSample(minimumSampleText);
  const topHypothesis: string =
    snapshot?.top_hypothesis ??
    (allSubmissions.find((s) => s.step_number === 1)?.content as any)?.top_hypothesis ??
    "";

  // ---- Status ------------------------------------------------------------
  const forcedReadOnly = readOnly || isStepCompleted;
  const evidenceStatus: "collecting" | "ready_for_verdict" =
    forcedReadOnly
      ? "ready_for_verdict"
      : content.evidence_status === "ready_for_verdict"
        ? "ready_for_verdict"
        : "collecting";

  // ---- Seed metric_results ----------------------------------------------
  const step3Metrics = Array.isArray(step3?.metrics_to_collect)
    ? (step3.metrics_to_collect as Array<{ id?: string; label?: string; checked?: boolean }>)
    : null;

  const seededDefs = useMemo(
    () => buildStep4MetricDefinitions(normExpId, step3Metrics),
    [normExpId, JSON.stringify(step3Metrics)],
  );

  // Initialize metric_results from server content, legacy blob, or seed.
  const seedRef = useRef(false);
  useEffect(() => {
    if (forcedReadOnly) return;
    if (seedRef.current) return;
    const existing = content.metric_results;
    if (Array.isArray(existing) && existing.length > 0) {
      seedRef.current = true;
      return;
    }
    // Map legacy `metrics` blob if present
    const legacyMetrics = content.metrics ?? null;
    const seeded = legacyMetrics
      ? mapLegacyToMetricResults(legacyMetrics, seededDefs)
      : seededDefs;
    const patch: Record<string, any> = {
      metric_results: seeded,
      metrics_initialized_for_experiment: normExpId,
      metrics_user_modified: false,
      metrics_seed_source: legacyMetrics ? "legacy" : (step3Metrics ? "step3" : (normExpId === "custom" ? "fallback" : "preset")),
    };
    if (!content.evidence_status) patch.evidence_status = "collecting";
    if (!content.collection_started_at) patch.collection_started_at = new Date().toISOString();
    Object.entries(patch).forEach(([k, v]) => set(k, v));
    seedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forcedReadOnly, normExpId]);

  // "Sorgenti di traffico principali" è un campo informativo (testuale) di Step 3,
  // non una metrica numerica confrontabile con la soglia: la escludiamo dalla
  // selezione di "primary" e dal confronto soglia.
  const isSourceLabel = (label?: string) =>
    /sorgent/i.test(label ?? "") && /traffic/i.test(label ?? "");

  const metricResults: MetricResult[] = useMemo(() => {
    let list: MetricResult[] = Array.isArray(content.metric_results)
      ? [...content.metric_results]
      : seededDefs;
    // Se la primary corrente è una "sorgenti di traffico", riassegnala a
    // un'altra metrica numerica.
    const primaryIdx = list.findIndex((m) => m.is_primary);
    if (primaryIdx >= 0 && isSourceLabel(list[primaryIdx]?.label)) {
      list = list.map((m) => ({ ...m, is_primary: false }));
    }
    // Garantisci che esista sempre una primary, mai su una sorgente.
    if (!list.some((m) => m.is_primary) && list.length > 0) {
      const activIdx = list.findIndex((m) =>
        m.label.toLowerCase().includes("attivaz")
      );
      const fallbackIdx = (() => {
        for (let i = list.length - 1; i >= 0; i--) {
          if (!isSourceLabel(list[i].label)) return i;
        }
        return list.length - 1;
      })();
      const idx = activIdx >= 0 ? activIdx : fallbackIdx;
      list = list.map((m, i) => (i === idx ? { ...m, is_primary: true } : m));
    }
    return list;
  }, [content.metric_results, seededDefs]);

  const updateMetric = (id: string, patch: Partial<MetricResult>) => {
    const next = metricResults.map((m) => (m.id === id ? { ...m, ...patch } : m));
    set("metric_results", next);
    if (!content.metrics_user_modified) set("metrics_user_modified", true);
  };
  // ---- Threshold assessment ---------------------------------------------
  const storedAssessment: ThresholdAssessment | null = content.threshold_assessment ?? null;
  const computedAssessment = useMemo(
    () => parseThreshold(thresholdText, metricResults),
    [thresholdText, metricResults],
  );
  // Base = parser output. Overlay any non-null field the user has already
  // edited/persisted, so selects and inputs in "Conferma come misurare la
  // soglia" don't reset on every render. `manually_confirmed` still gates
  // the final state, but no longer governs which values appear in inputs.
  // Enrich con default sensati derivati da Step 2 (primary metric + operatore)
  // così i dropdown non appaiono mai vuoti quando Step 2 ha definito la soglia.
  const primaryMetricId = metricResults.find((m) => m.is_primary)?.id ?? null;
  const enrichedComputed: ThresholdAssessment = {
    ...computedAssessment,
    mapped_metric_id: computedAssessment.mapped_metric_id ?? primaryMetricId,
    operator: computedAssessment.operator ?? "gte",
  };
  const assessment: ThresholdAssessment = storedAssessment
    ? {
        ...enrichedComputed,
        ...(Object.fromEntries(
          Object.entries(storedAssessment).filter(
            ([, v]) => v !== null && v !== undefined,
          ),
        ) as Partial<ThresholdAssessment>),
      }
    : enrichedComputed;

  const updateAssessment = (patch: Partial<ThresholdAssessment>) => {
    const next: ThresholdAssessment = { ...assessment, ...patch };
    set("threshold_assessment", next);
  };

  // ---- Qualitative evidence / objections / insights / links --------------
  const qualitativeEvidence: QualitativeEvidence[] = Array.isArray(content.qualitative_evidence)
    ? content.qualitative_evidence
    : [];
  const typedLinks: TypedEvidenceLink[] = Array.isArray(content.typed_evidence_links)
    ? content.typed_evidence_links
    : [];
  const objections: ObjectionEntry[] = normalizeObjections(content.objections);
  const insights: string[] = Array.isArray(content.insights)
    ? content.insights
    : typeof content.insights === "string" && content.insights.trim()
      ? content.insights.split("\n").map((s: string) => s.trim()).filter(Boolean)
      : [];

  // ---- Sample collected: somma dei "contatti" dal daily log di Step 3 ----
  const step3DailyLogs: any[] = Array.isArray(step3?.daily_logs) ? step3.daily_logs : [];
  const sampleCollected: number | null = step3DailyLogs.length > 0
    ? step3DailyLogs.reduce((acc, l) => acc + (Number((l as any)?.contatti) || 0), 0)
    : null;

  // ---- Evidence quality (always recomputed at render) --------------------
  const quality: EvidenceQualityAssessment = useMemo(
    () =>
      evaluateEvidenceQuality({
        metricResults,
        threshold: assessment,
        qualitativeEvidence,
        typedLinks,
        sampleCollected,
        sampleTarget,
      }),
    [metricResults, assessment, qualitativeEvidence, typedLinks, sampleCollected, sampleTarget],
  );

  // ---- Gaps -------------------------------------------------------------
  const gaps = useMemo(
    () => computeStep4Gaps({ metricResults, threshold: assessment, qualitativeEvidence, typedLinks }),
    [metricResults, assessment, qualitativeEvidence, typedLinks],
  );

  // ---- Derived metrics --------------------------------------------------
  const derived = useMemo(() => computeDerivedMetrics(metricResults, normExpId), [metricResults, normExpId]);

  // ---- Save / Complete handlers -----------------------------------------
  const [pending, setPending] = useState<null | "draft" | "complete">(null);
  const busy = saving || pending !== null;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [doubleConfirm, setDoubleConfirm] = useState(false);

  // ---- AI data interpretation state ---------------------------------------
  type DataInterpretationResult = {
    verdict_signal: "green" | "yellow" | "red";
    interpretation: string;
    bias_check: {
      detected: boolean;
      pattern?: string;
      reality?: string;
    };
    missing_for_decision: string[];
    one_line_signal: string;
  };

  const [interpretationResult, setInterpretationResult] = useState<DataInterpretationResult | null>(
    (content.interpretation_result as DataInterpretationResult) ?? null
  );
  const [interpretationLoading, setInterpretationLoading] = useState(false);
  const [interpretationError, setInterpretationError] = useState<string | null>(null);

  // ---- Helper: build metrics summary for AI -----------------------------
  const buildMetricsSummary = (): string => {
    const results: any[] = Array.isArray(content.metric_results) ? content.metric_results : [];
    if (results.length === 0) return "";
    return results
      .filter((m: any) => m.value != null)
      .map((m: any) => `${m.label}: ${m.value}${m.is_primary ? " (metrica principale)" : ""}`)
      .join(" | ");
  };

  const getPrimaryMetric = (): { label: string; value: number | null } => {
    const results: any[] = Array.isArray(content.metric_results) ? content.metric_results : [];
    const primary = results.find((m: any) => m.is_primary);
    return {
      label: primary?.label ?? "Risultato principale",
      value: primary?.value ?? null,
    };
  };

  const runDataInterpretation = async () => {
    setInterpretationLoading(true);
    setInterpretationError(null);
    try {
      const step2Content = allSubmissions?.find((s: any) => s.step_number === 2)?.content ?? {};
      const primary = getPrimaryMetric();
      const metricsSummary = buildMetricsSummary();
      const thresholdValue = (step2Content as any).success_threshold ?? "";

      const qualEvidence: any[] = Array.isArray(content.qualitative_evidence)
        ? content.qualitative_evidence
        : [];
      const qualNotes = qualEvidence
        .filter((e: any) => (e.quote ?? "").trim())
        .map((e: any) => `"${e.quote}"${e.source ? ` — ${e.source}` : ""}`)
        .join("\n");

      const evidenceLevel = (content.evidence_quality as any)?.level ?? undefined;

      const thresholdMet = primary.value != null && primary.value > 0;

      const { data, error } = await supabase.functions.invoke("v2-sprint-data-interpretation", {
        body: {
          hypothesis: topHypothesis ?? "",
          success_threshold: thresholdValue || ((step2Content as any).success_threshold ?? ""),
          target: (step2Content as any).target ?? "",
          metrics_summary: metricsSummary,
          threshold_met: thresholdMet,
          primary_metric_label: primary.label,
          primary_metric_value: primary.value,
          qualitative_notes: qualNotes || undefined,
          evidence_level: evidenceLevel,
        },
      });
      if (error || !data) throw new Error("interpretation_failed");
      setInterpretationResult(data as DataInterpretationResult);
      set("interpretation_result", data);
    } catch {
      setInterpretationError("Non è stato possibile interpretare i dati. Riprova.");
    } finally {
      setInterpretationLoading(false);
    }
  };

  // Auto-run interpretation quando cambiano i dati principali (debounced).
  const primaryForAI = getPrimaryMetric();
  const metricsSummaryForAI = buildMetricsSummary();
  const interpretationRunKeyRef = useRef<string>("");
  useEffect(() => {
    if (disabled) return;
    if (primaryForAI.value == null) return;
    const key = `${primaryForAI.label}|${primaryForAI.value}|${metricsSummaryForAI}|${topHypothesis}`;
    if (key === interpretationRunKeyRef.current) return;
    const timer = setTimeout(() => {
      if (interpretationLoading) return;
      interpretationRunKeyRef.current = key;
      runDataInterpretation();
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryForAI.value, primaryForAI.label, metricsSummaryForAI, topHypothesis, disabled]);

  const buildPersistPatch = (extra: Record<string, any> = {}): Record<string, any> => {
    const now = new Date().toISOString();
    const freshQuality = evaluateEvidenceQuality({
      metricResults,
      threshold: assessment,
      qualitativeEvidence,
      typedLinks,
      sampleCollected,
      sampleTarget,
    });
    return {
      evidence_status: extra.evidence_status ?? "collecting",
      collection_started_at: content.collection_started_at ?? now,
      collection_last_saved_at: now,
      metric_results: metricResults,
      threshold_assessment: assessment,
      qualitative_evidence: qualitativeEvidence,
      typed_evidence_links: typedLinks,
      objections,
      insights,
      evidence_quality: freshQuality,
      metrics_initialized_for_experiment: content.metrics_initialized_for_experiment ?? normExpId,
      metrics_user_modified: !!content.metrics_user_modified,
      metrics_seed_source: content.metrics_seed_source ?? "preset",
      // ---- Legacy mirrors (Step 5 compatibility) ----
      metrics: buildLegacyMetricsMirror(metricResults, content.metrics),
      real_quotes: buildLegacyRealQuotesMirror(qualitativeEvidence, content.real_quotes),
      interpretation_result: content.interpretation_result ?? null,
      ...extra,
    };
  };

  const handleSaveDraft = async () => {
    if (busy) return;
    setPending("draft");
    try {
      const patch = buildPersistPatch({ evidence_status: "collecting" });
      // Sync legacy evidence_links column too
      const mirroredLinks = buildLegacyEvidenceLinksMirror(typedLinks, links);
      onLinksChange(mirroredLinks);
      await saveDraft(patch);
      toast.success("Progressi salvati.");
    } finally {
      setPending(null);
    }
  };

  const handleAttemptComplete = () => {
    if (busy) return;
    setDoubleConfirm(false);
    setConfirmOpen(true);
  };

  const handleConfirmComplete = async () => {
    if (busy) return;
    const primary = metricResults.find((m) => m.is_primary);
    const hasPrimary = primary && primary.value != null;
    const hasAnyProof =
      qualitativeEvidence.some((q) => (q.quote ?? "").trim()) ||
      typedLinks.some((l) => (l.url ?? "").trim());
    if ((!hasPrimary || !hasAnyProof) && !doubleConfirm) {
      setDoubleConfirm(true);
      return;
    }
    setPending("complete");
    try {
      const now = new Date().toISOString();
      const patch = buildPersistPatch({
        evidence_status: "ready_for_verdict",
        ready_for_verdict_at: now,
      });
      const mirroredLinks = buildLegacyEvidenceLinksMirror(typedLinks, links);
      onLinksChange(mirroredLinks);
      await completeStep(patch);
      setConfirmOpen(false);
    } finally {
      setPending(null);
    }
  };

  // ---- Refs for "Cosa manca" scroll ------------------------------------
  const metricsRef = useRef<HTMLDivElement>(null);
  const thresholdRef = useRef<HTMLDivElement>(null);
  const evidenceRef = useRef<HTMLDivElement>(null);

  const scrollToGap = (g: string) => {
    const low = g.toLowerCase();
    const target = low.includes("metrica principale") || low.includes("risultato principale")
      ? metricsRef.current
      : low.includes("soglia")
        ? thresholdRef.current
        : evidenceRef.current;
    if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  // =======================================================================
  // RENDER
  // =======================================================================
  return (
    <div className="space-y-5">
      <StepIntroCard text="Ora hai dati. Registra i numeri onestamente: attivazioni, retention a 48h, feedback qualitativo. Non interpretare ancora — annota quello che è successo davvero. L'AI ti aiuta a leggere i pattern, ma le evidenze le inserisci tu." />
      <div className="rounded-xl border border-white/8 bg-white/[0.03] px-4 py-3 text-sm text-slate-400 leading-relaxed">
        Non aspettare di avere dati perfetti. Anche 3 conversazioni significative o un pattern chiaro su 5 risposte sono dati validi — inserisci quello che hai.
      </div>
      {/* Stato badge */}
      <div className="flex items-center gap-2">
        <span className={cn(
          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wider font-medium border",
          evidenceStatus === "ready_for_verdict"
            ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-200"
            : "border-secondary/40 bg-secondary/10 text-secondary",
        )}>
          {evidenceStatus === "ready_for_verdict" ? (
            <><CheckCircle2 className="h-3.5 w-3.5" /> Evidenze confermate</>
          ) : (
            <><PlayCircle className="h-3.5 w-3.5" /> Raccolta evidenze</>
          )}
        </span>
      </div>

      {/* Snapshot del test (read-only) */}
      <div className="rounded-xl border border-secondary/30 bg-secondary/5 p-4 space-y-2">
        <p className="text-xs uppercase tracking-wider text-secondary font-medium">
          Stai registrando i risultati di questo test
        </p>
        {topHypothesis && (
          <p className="text-sm text-slate-100 leading-snug">
            <span className="text-slate-300">Ipotesi: </span>{topHypothesis}
          </p>
        )}
        <div className="grid gap-2 sm:grid-cols-2 text-sm text-slate-100">
          <p><span className="text-slate-300">Esperimento: </span>{experimentLabel}</p>
          {snapshot?.target && <p><span className="text-slate-300">Target: </span>{snapshot.target}</p>}
          {snapshot?.channel && <p><span className="text-slate-300">Canale: </span>{snapshot.channel}</p>}
          {snapshot?.action && <p><span className="text-slate-300">Azione osservata: </span>{snapshot.action}</p>}
          {thresholdText && <p><span className="text-slate-300">Soglia: </span>{thresholdText}</p>}
          {sampleTarget != null && <p><span className="text-slate-300">Campione previsto: </span>{sampleTarget}</p>}
          {snapshot?.duration_days != null && <p><span className="text-slate-300">Durata: </span>{snapshot.duration_days} giorni</p>}
        </div>
      </div>

      {/* Cosa manca */}
      {!forcedReadOnly && gaps.length > 0 && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 space-y-2">
          <p className="text-xs uppercase tracking-wider text-amber-300 font-medium">
            Prima del verdetto ti manca
          </p>
          <ul className="space-y-1">
            {gaps.map((g, i) => (
              <li key={i}>
                <button
                  type="button"
                  onClick={() => scrollToGap(g)}
                  className="text-sm text-amber-100 hover:text-amber-50 text-left underline-offset-2 hover:underline"
                >
                  · {g}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Metriche dinamiche */}
      <div ref={metricsRef} className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-foreground">Risultati raccolti</Label>
          {content.metrics_seed_source && (
            <span className="text-[10px] uppercase tracking-wider text-slate-300">
              {content.metrics_seed_source === "step3"
                ? "Da Step 3"
                : content.metrics_seed_source === "preset"
                  ? `Da preset: ${experimentLabel}`
                  : content.metrics_seed_source === "legacy"
                    ? "Da versione precedente"
                    : "Piano universale"}
            </span>
          )}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {metricResults.filter((m) => !isSourceLabel(m.label)).map((m) => (
            <div
              key={m.id}
              className={cn(
                "rounded-xl border p-3 space-y-2",
                m.is_primary
                  ? "border-primary/50 bg-primary/5"
                  : "border-white/10 bg-white/[0.04]",
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-0.5">
                  <p className="text-sm font-semibold text-slate-100">
                    {m.label}
                    {m.is_primary && (
                      <span className="ml-1.5 text-[10px] uppercase tracking-wider text-primary">· principale</span>
                    )}
                  </p>
                  {m.available_from_step3 && (
                    <p className="text-[10px] text-secondary/90 font-medium">già pronta in Step 3</p>
                  )}
                </div>
                </div>
              <Input
                type="number"
                min={0}
                disabled={forcedReadOnly}
                className={inputClass}
                value={m.value ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  updateMetric(m.id, { value: v === "" ? null : Number(v) });
                }}
                placeholder="0"
              />
            </div>
          ))}
        </div>

        {/* Principali fonti di traffico — campo testo separato (non è una metrica numerica) */}
        {metricResults.some((m) => isSourceLabel(m.label)) && (
          <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 space-y-1.5">
            <p className="text-sm font-semibold text-slate-100">Principali fonti di traffico</p>
            <p className="text-xs text-slate-300">Da dove sono arrivate le persone?</p>
            <Textarea
              rows={2}
              disabled={forcedReadOnly}
              className={inputClass}
              value={(content.traffic_sources_note as string) ?? ""}
              onChange={(e) => set("traffic_sources_note", e.target.value)}
              placeholder="Es. LinkedIn DM — 18 persone / Community Slack — 5 persone"
            />
          </div>
        )}
        {derived.length > 0 && (
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 space-y-1">
            <p className="text-[11px] uppercase tracking-wider text-slate-300 font-medium">
              Calcolati automaticamente
            </p>
            {derived.map((d) => (
              <p key={d.id} className="text-sm text-foreground">
                <span className="text-slate-300">{d.label}: </span>
                <span className="font-semibold">{d.value}{d.unit === "percentage" ? "%" : ""}</span>
              </p>
            ))}
          </div>
        )}
      </div>

      {/* Confronto soglia */}
      {thresholdText && (
        <div ref={thresholdRef} className="rounded-xl border border-white/10 bg-slate-950/40 p-4 space-y-3">
          <p className="text-xs uppercase tracking-wider text-slate-100 font-medium">
            Confronto con la soglia
          </p>
          <ThresholdComparison
            assessment={assessment}
            metricResults={metricResults}
            sourceText={thresholdText}
            disabled={forcedReadOnly}
            onConfirmManual={updateAssessment}
          />
        </div>
      )}

      {/* Campione minimo */}
      <MinimumSampleBlock
        sampleTarget={sampleTarget}
        sampleCollected={sampleCollected}
        rawSampleText={minimumSampleText}
        step2Sample={step2MinimumSample}
        overrideValue={minimumSampleOverride}
        disabled={forcedReadOnly}
        onOverride={(val) => set("minimum_sample_override", val)}
      />


      {/* AI data interpretation */}
      {getPrimaryMetric().value != null && !disabled && (
        <div className="mt-6 space-y-4">
          <div className="border-t border-slate-700/50 pt-4">
            <p className="text-xs uppercase tracking-wider text-slate-300 font-medium mb-2">
              Interpretazione dei dati
            </p>
            {interpretationLoading && !interpretationResult && (
              <p className="text-sm text-slate-300 py-2">Analisi in corso…</p>
            )}
            {interpretationError && (
              <p className="text-sm text-red-400 mt-2">{interpretationError}</p>
            )}
            {!interpretationLoading && !interpretationResult && !interpretationError && (
              <p className="text-sm text-slate-300 py-2">
                L'analisi apparirà qui automaticamente non appena i dati sono pronti.
              </p>
            )}
          </div>

          {interpretationResult && (
            <div className="rounded-xl border overflow-hidden"
              style={{
                borderColor: interpretationResult.verdict_signal === "green"
                  ? "rgb(22 163 74 / 0.4)"
                  : interpretationResult.verdict_signal === "red"
                  ? "rgb(220 38 38 / 0.4)"
                  : "rgb(217 119 6 / 0.4)"
              }}
            >
              {/* Signal header */}
              <div className={`px-4 py-3 flex items-center justify-between ${
                interpretationResult.verdict_signal === "green"
                  ? "bg-emerald-900/30"
                  : interpretationResult.verdict_signal === "red"
                  ? "bg-red-900/30"
                  : "bg-amber-900/30"
              }`}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">
                    {interpretationResult.verdict_signal === "green" ? "🟢" : interpretationResult.verdict_signal === "red" ? "🔴" : "🟡"}
                  </span>
                  <span className={`text-sm font-medium ${
                    interpretationResult.verdict_signal === "green"
                      ? "text-emerald-300"
                      : interpretationResult.verdict_signal === "red"
                      ? "text-red-300"
                      : "text-amber-300"
                  }`}>
                    {interpretationResult.verdict_signal === "green"
                      ? "Segnale positivo"
                      : interpretationResult.verdict_signal === "red"
                      ? "Segnale negativo"
                      : "Segnale inconclusivo"}
                  </span>
                </div>
                <p className="text-xs text-slate-300 text-right max-w-[55%]">
                  {interpretationResult.one_line_signal}
                </p>
              </div>

              <div className="p-4 space-y-4 bg-slate-800/40">
                {/* Interpretation */}
                <p className="text-sm text-slate-300 leading-relaxed">
                  {interpretationResult.interpretation}
                </p>

                {/* Bias check */}
                {interpretationResult.bias_check.detected && (
                  <div className="rounded-lg bg-red-900/20 border border-red-800/40 p-3 space-y-1.5">
                    <p className="text-xs font-semibold text-red-300">⚠ Possibile auto-convincimento rilevato</p>
                    {interpretationResult.bias_check.pattern && (
                      <p className="text-xs text-slate-300">
                        <span className="text-slate-300">Pattern: </span>
                        {interpretationResult.bias_check.pattern}
                      </p>
                    )}
                    {interpretationResult.bias_check.reality && (
                      <p className="text-xs text-slate-300">
                        <span className="text-slate-300">Realtà: </span>
                        {interpretationResult.bias_check.reality}
                      </p>
                    )}
                  </div>
                )}

                {/* Missing for decision */}
                {interpretationResult.missing_for_decision.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs font-medium text-slate-300">Per poter decidere manca ancora:</p>
                    <ul className="space-y-1">
                      {interpretationResult.missing_for_decision.map((item, i) => (
                        <li key={i} className="flex gap-2 text-xs text-slate-300">
                          <span className="text-amber-400 shrink-0">→</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Evidenze qualitative */}
      <div ref={evidenceRef} className="space-y-3">
        <Label className="text-sm font-semibold text-foreground">Evidenze qualitative</Label>
        <p className="text-xs text-slate-300">
          Citazioni, reazioni o osservazioni concrete. Scegli le più rappresentative: nello Step 5 useremo soprattutto le tre più forti.
        </p>
        <QualitativeEvidenceList
          items={qualitativeEvidence}
          disabled={forcedReadOnly}
          onChange={(next) => set("qualitative_evidence", next)}
        />
      </div>

      {/* Obiezioni & insight */}
      <div className="grid gap-4 sm:grid-cols-2">
        <ObjectionsField
          items={objections}
          disabled={forcedReadOnly}
          onChange={(next) => set("objections", next)}
        />
        <StringListField
          label="Insight emersi"
          items={insights}
          disabled={forcedReadOnly}
          onChange={(next) => set("insights", next)}
          placeholder="Es: cercano una soluzione molto più semplice"
        />
      </div>

      {/* Link tipizzati */}
      <TypedEvidenceLinks
        items={typedLinks}
        disabled={forcedReadOnly}
        onChange={(next) => set("typed_evidence_links", next)}
      />

      {/* Qualità evidenze */}
      <EvidenceQualityCard quality={quality} />

      {/* CTA interne */}
      {!forcedReadOnly && (
        <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={handleSaveDraft}
            className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
          >
            {pending === "draft" ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Salvataggio…</>
            ) : (
              "Salva progressi"
            )}
          </Button>
          <Button
            type="button"
            disabled={busy}
            onClick={handleAttemptComplete}
            className="bg-primary hover:bg-primary/90"
          >
            {pending === "complete" ? (
              <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Completamento…</>
            ) : (
              <>Conferma evidenze e passa al verdetto <ArrowRight className="h-4 w-4 ml-1.5" /></>
            )}
          </Button>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={(o) => { setConfirmOpen(o); if (!o) setDoubleConfirm(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-foreground">Prima di passare al verdetto</AlertDialogTitle>
            <AlertDialogDescription className="text-foreground space-y-2">
              <span className="block">Riepilogo evidenze:</span>
              <span className="block text-sm">
                · Risultato principale: {(() => {
                  const p = metricResults.find((m) => m.is_primary);
                  return p?.value != null ? `${p.label}: ${p.value}` : "non ancora registrato";
                })()}
              </span>
              <span className="block text-sm">· Soglia: {thresholdText || "non definita"}</span>
              <span className="block text-sm">
                · Stato soglia: {assessment.target_value != null && assessment.mapped_metric_id
                  ? "confrontabile"
                  : "da confermare manualmente"}
              </span>
              {sampleTarget != null && (
                <span className="block text-sm">· Campione: {sampleCollected ?? 0} / {sampleTarget}</span>
              )}
              <span className="block text-sm">· Qualità evidenze: {quality.level === "weak" ? "Debole" : quality.level === "sufficient" ? "Sufficiente" : "Solida"}</span>
              <span className="block text-sm">· Citazioni: {qualitativeEvidence.filter((q) => q.quote?.trim()).length}</span>
              <span className="block text-sm">· Prove/link: {typedLinks.filter((l) => l.url?.trim()).length}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {doubleConfirm && (
            <div className="rounded-md border border-amber-400/40 bg-amber-500/10 p-3 text-sm text-amber-100">
              Mancano il risultato principale o qualsiasi prova concreta. Vuoi davvero passare al verdetto?
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Torna alla raccolta</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmComplete} disabled={busy}>
              {doubleConfirm ? "Continua comunque" : "Sì, passa al verdetto"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---- Step 4 sub-components ----

function MinimumSampleBlock({
  sampleTarget,
  sampleCollected,
  rawSampleText,
  step2Sample,
  overrideValue,
  disabled,
  onOverride,
}: {
  sampleTarget: number | null;
  sampleCollected: number | null;
  rawSampleText: string | number | null;
  step2Sample: string | number | null;
  overrideValue: string | number | null;
  disabled: boolean;
  onOverride: (val: number | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(
    overrideValue != null ? String(overrideValue) : "",
  );

  const openEditor = () => {
    setDraft(
      overrideValue != null
        ? String(overrideValue)
        : sampleTarget != null
          ? String(sampleTarget)
          : "",
    );
    setEditing(true);
  };

  const save = () => {
    const n = draft.trim() === "" ? null : Number(draft);
    onOverride(n != null && !Number.isNaN(n) && n > 0 ? n : null);
    setEditing(false);
  };

  const clearOverride = () => {
    onOverride(null);
    setDraft("");
    setEditing(false);
  };

  if (sampleTarget == null) return null;

  // Defined state
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4 space-y-2">
      <div className="flex items-center justify-between gap-3 text-sm text-foreground">
        <span><span className="text-slate-300">Campione previsto: </span>{sampleTarget}</span>
        <div className="flex items-center gap-3">
          <span><span className="text-slate-300">Raccolto: </span>{sampleCollected ?? "—"}</span>
          {!disabled && !editing && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
              onClick={openEditor}
            >
              Modifica
            </Button>
          )}
        </div>
      </div>
      <Progress
        value={sampleCollected != null ? Math.min(100, (sampleCollected / sampleTarget) * 100) : 0}
        className="h-2"
      />
      {sampleCollected != null && sampleCollected < sampleTarget && (
        <p className="text-xs text-amber-200">
          Il campione è ancora inferiore a quello previsto. Il risultato può essere indicativo, ma non ancora affidabile.
        </p>
      )}
      {overrideValue != null && (
        <p className="text-[11px] text-slate-300">
          Valore impostato in Step 4. Step 2 indicava: {step2Sample ? String(step2Sample) : "—"}.
        </p>
      )}
      {editing && !disabled && (
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Input
            type="number"
            min={1}
            className={cn(inputClass, "h-9 w-32")}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Es. 200"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
            onClick={save}
          >
            Salva
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Annulla
          </Button>
          {overrideValue != null && (
            <Button type="button" size="sm" variant="ghost" onClick={clearOverride}>
              Usa valore Step 2
            </Button>
          )}
        </div>
      )}
    </div>
  );
}


function ThresholdComparison({
  assessment,
  metricResults,
  sourceText,
  disabled,
  onConfirmManual,
}: {
  assessment: ThresholdAssessment;
  metricResults: MetricResult[];
  sourceText: string;
  disabled: boolean;
  onConfirmManual: (patch: Partial<ThresholdAssessment>) => void;
}) {
  const [editing, setEditing] = useState(false);
  // Mostra la modalità manual solo se l'utente ha cliccato "Modifica" o se
  // mancano campi essenziali per il confronto (target/metrica/operatore).
  const canRenderComparison =
    !!assessment.mapped_metric_id &&
    !!assessment.operator &&
    assessment.target_value != null;
  const showManual =
    editing || (!canRenderComparison && !assessment.manually_confirmed);

  const mappedMetric = metricResults.find((m) => m.id === assessment.mapped_metric_id);
  const currentValue = mappedMetric?.value ?? null;
  const target = assessment.target_value;
  const op = assessment.operator;

  const reached = (() => {
    if (currentValue == null || target == null || !op) return null;
    if (op === "gte" || op === "gt") return op === "gte" ? currentValue >= target : currentValue > target;
    if (op === "lte" || op === "lt") return op === "lte" ? currentValue <= target : currentValue < target;
    return currentValue === target;
  })();

  const ratioPct = (() => {
    if (currentValue == null || !target || target <= 0) return null;
    return Math.round((currentValue / target) * 100);
  })();

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-100">
          <span className="text-slate-300">Soglia definita: </span>{sourceText}
        </p>
        {!disabled && !showManual && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground shrink-0"
            onClick={() => setEditing(true)}
          >
            Modifica
          </Button>
        )}
      </div>
      {showManual && (
        <div className="rounded-lg border border-amber-400/40 bg-amber-500/5 p-3 space-y-2">
          <p className="text-sm text-amber-100">Conferma come misurare la soglia</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <select
              disabled={disabled}
              className={cn(inputClass, "h-9 rounded-md px-2")}
              value={assessment.mapped_metric_id ?? ""}
              onChange={(e) => onConfirmManual({ mapped_metric_id: e.target.value || null })}
            >
              <option value="">Scegli metrica…</option>
              {metricResults
                .filter((m) => !/sorgent/i.test(m.label) || !/traffic/i.test(m.label))
                .map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
            </select>
            <select
              disabled={disabled}
              className={cn(inputClass, "h-9 rounded-md px-2")}
              value={assessment.operator ?? ""}
              onChange={(e) => onConfirmManual({ operator: (e.target.value || null) as ThresholdAssessment["operator"] })}
            >
              <option value="">Scegli operatore…</option>
              <option value="gte">almeno (≥)</option>
              <option value="gt">più di (&gt;)</option>
              <option value="lte">al massimo (≤)</option>
              <option value="lt">meno di (&lt;)</option>
              <option value="equal">uguale a</option>
            </select>
            <Input
              type="number"
              disabled={disabled}
              className={inputClass}
              value={assessment.target_value ?? ""}
              onChange={(e) => onConfirmManual({ target_value: e.target.value === "" ? null : Number(e.target.value) })}
              placeholder="Valore soglia"
            />
            <Input
              disabled={disabled}
              className={inputClass}
              value={assessment.timeframe_text ?? ""}
              onChange={(e) => onConfirmManual({ timeframe_text: e.target.value || null })}
              placeholder="Periodo (opzionale)"
            />
          </div>
          {!disabled && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
                onClick={() => {
                  onConfirmManual({ manually_confirmed: true });
                  setEditing(false);
                }}
                disabled={!assessment.mapped_metric_id || !assessment.operator || assessment.target_value == null}
              >
                {assessment.manually_confirmed ? "Salva modifiche" : "Conferma mapping"}
              </Button>
              {editing && assessment.manually_confirmed && (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setEditing(false)}
                >
                  Annulla
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      {!showManual && mappedMetric && target != null && (
        <>
          <p className="text-sm text-slate-100">
            <span className="text-slate-300">Metrica confrontata: </span>{mappedMetric.label}
          </p>
          <p className="text-sm text-slate-100">
            <span className="text-slate-300">Risultato attuale: </span>{currentValue ?? "—"}
          </p>
          {reached === true && (
            <p className="text-sm font-semibold text-emerald-300">Soglia raggiunta</p>
          )}
          {reached === false && ratioPct != null && ratioPct >= 80 && (
            <p className="text-sm font-semibold text-amber-200">
              Quasi raggiunta ({ratioPct}% del target)
            </p>
          )}
          {reached === false && (ratioPct == null || ratioPct < 80) && (
            <p className="text-sm font-semibold text-rose-300">
              Sotto soglia{currentValue != null ? ` — mancano ${Math.max(0, target - currentValue)}` : ""}
            </p>
          )}
          {currentValue == null && (
            <p className="text-xs text-slate-300">Inserisci il valore della metrica per vedere il confronto.</p>
          )}
        </>
      )}
    </div>
  );
}

function QualitativeEvidenceList({
  items,
  disabled,
  onChange,
}: {
  items: QualitativeEvidence[];
  disabled: boolean;
  onChange: (next: QualitativeEvidence[]) => void;
}) {
  const add = () => {
    onChange([
      ...items,
      { id: crypto.randomUUID(), quote: "", source: "", sentiment: "neutral" },
    ]);
  };
  const update = (id: string, patch: Partial<QualitativeEvidence>) => {
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };
  const remove = (id: string) => onChange(items.filter((it) => it.id !== id));

  return (
    <div className="space-y-2">
      {items.length === 0 && !disabled && (
        <p className="text-xs text-slate-300">Nessuna evidenza qualitativa ancora.</p>
      )}
      {items.map((it) => (
        <div key={it.id} className="rounded-lg border border-white/10 bg-white/[0.04] p-3 space-y-2">
          <Textarea
            rows={2}
            disabled={disabled}
            className={inputClass}
            value={it.quote}
            onChange={(e) => update(it.id, { quote: e.target.value })}
            placeholder='"Lo userei subito" — citazione testuale o reazione osservata'
          />
          <div className="grid gap-2 sm:grid-cols-3">
            <Input
              disabled={disabled}
              className={inputClass}
              value={it.source}
              onChange={(e) => update(it.id, { source: e.target.value })}
              placeholder="Fonte (nome, ruolo, canale)"
            />
            <select
              disabled={disabled}
              className={cn(inputClass, "h-10 rounded-md px-2")}
              value={it.sentiment}
              onChange={(e) => update(it.id, { sentiment: e.target.value as QualitativeEvidence["sentiment"] })}
            >
              <option value="positive">Positivo</option>
              <option value="neutral">Neutro</option>
              <option value="negative">Negativo</option>
            </select>
            <Input
              disabled={disabled}
              className={inputClass}
              value={it.link ?? ""}
              onChange={(e) => update(it.id, { link: e.target.value })}
              placeholder="Link (opzionale)"
            />
          </div>
          {!disabled && (
            <div className="flex justify-end">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => remove(it.id)}
                className="text-rose-300 hover:text-rose-200"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Rimuovi
              </Button>
            </div>
          )}
        </div>
      ))}
      {!disabled && (
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={add}
            className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
          >
            <Plus className="h-4 w-4 mr-1" /> Aggiungi evidenza qualitativa
          </Button>
          {items.length > 8 && (
            <p className="text-xs text-amber-200">
              Hai più di 8 evidenze. Tieni le più rappresentative.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function StringListField({
  label,
  items,
  disabled,
  onChange,
  placeholder,
}: {
  label: string;
  items: string[];
  disabled: boolean;
  onChange: (next: string[]) => void;
  placeholder?: string;
}) {
  const update = (i: number, v: string) => {
    const next = [...items];
    next[i] = v;
    onChange(next);
  };
  const remove = (i: number) => onChange(items.filter((_, idx) => idx !== i));
  const add = () => onChange([...items, ""]);
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-foreground">{label}</Label>
      {items.map((v, i) => (
        <div key={i} className="flex gap-2">
          <Input
            disabled={disabled}
            className={inputClass}
            value={v}
            onChange={(e) => update(i, e.target.value)}
            placeholder={placeholder}
          />
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(i)}
              aria-label="Rimuovi"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
        >
          <Plus className="h-4 w-4 mr-1" /> Aggiungi
        </Button>
      )}
    </div>
  );
}

// ---- Objections (structured: category + optional note) --------------------
type ObjectionType =
  | "pricing_friction"
  | "wrong_target"
  | "message_unclear"
  | "timing"
  | "weak_problem"
  | "trust"
  | "other";

interface ObjectionEntry {
  type: ObjectionType;
  note?: string;
}

const OBJECTION_CATEGORIES: { type: ObjectionType; label: string }[] = [
  { type: "pricing_friction", label: "Troppo caro / non vale il prezzo" },
  { type: "wrong_target", label: "Non mi serve / ho già una soluzione" },
  { type: "message_unclear", label: "Non ho capito cosa fa" },
  { type: "timing", label: "Non è il momento giusto" },
  { type: "weak_problem", label: "Non è una priorità per me" },
  { type: "trust", label: "Non mi fido / non ti conosco" },
  { type: "other", label: "Altro" },
];

function normalizeObjections(raw: any): ObjectionEntry[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item): ObjectionEntry | null => {
        if (!item) return null;
        if (typeof item === "string") {
          const t = item.trim();
          if (!t) return null;
          return { type: "other", note: t };
        }
        if (typeof item === "object" && typeof item.type === "string") {
          const validTypes = OBJECTION_CATEGORIES.map((c) => c.type);
          const type = (validTypes.includes(item.type) ? item.type : "other") as ObjectionType;
          const note = typeof item.note === "string" ? item.note : undefined;
          return { type, note };
        }
        return null;
      })
      .filter((x): x is ObjectionEntry => !!x);
  }
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((t) => ({ type: "other" as ObjectionType, note: t }));
  }
  return [];
}

function ObjectionsField({
  items,
  disabled,
  onChange,
}: {
  items: ObjectionEntry[];
  disabled: boolean;
  onChange: (next: ObjectionEntry[]) => void;
}) {
  const selectedTypes = new Set(items.map((i) => i.type));

  const toggle = (type: ObjectionType) => {
    if (disabled) return;
    if (selectedTypes.has(type)) {
      onChange(items.filter((i) => i.type !== type));
    } else {
      onChange([...items, { type, note: "" }]);
    }
  };

  const updateNote = (type: ObjectionType, note: string) => {
    onChange(items.map((i) => (i.type === type ? { ...i, note } : i)));
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-semibold text-foreground">Obiezioni ricorrenti</Label>
        <p className="text-xs text-slate-300 mt-1">
          Seleziona le categorie emerse più spesso. Campo opzionale.
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {OBJECTION_CATEGORIES.map(({ type, label }) => {
          const active = selectedTypes.has(type);
          return (
            <button
              key={type}
              type="button"
              disabled={disabled}
              onClick={() => toggle(type)}
              aria-pressed={active}
              className={cn(
                "px-3 py-1.5 rounded-full text-xs font-medium border transition-colors",
                active
                  ? "bg-primary/20 text-primary-foreground border-primary/60 ring-1 ring-primary/40"
                  : "bg-white/5 text-slate-200 border-white/15 hover:bg-white/10",
                disabled && "opacity-60 cursor-not-allowed"
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      {items.length > 0 && (
        <div className="space-y-2 pt-1">
          {items.map((obj) => {
            const cat = OBJECTION_CATEGORIES.find((c) => c.type === obj.type);
            return (
              <div key={obj.type} className="space-y-1">
                <Label className="text-xs text-slate-300">{cat?.label ?? obj.type}</Label>
                <Input
                  disabled={disabled}
                  className={inputClass}
                  value={obj.note ?? ""}
                  onChange={(e) => updateNote(obj.type, e.target.value)}
                  placeholder="Aggiungi dettaglio o cita le parole esatte..."
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



function TypedEvidenceLinks({
  items,
  disabled,
  onChange,
}: {
  items: TypedEvidenceLink[];
  disabled: boolean;
  onChange: (next: TypedEvidenceLink[]) => void;
}) {
  const add = () => onChange([
    ...items,
    { id: crypto.randomUUID(), url: "", label: "", type: "screenshot" },
  ]);
  const update = (id: string, patch: Partial<TypedEvidenceLink>) =>
    onChange(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const remove = (id: string) => onChange(items.filter((it) => it.id !== id));

  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-foreground">Prove e link</Label>
      <p className="text-xs text-slate-300">Screenshot, post pubblici, analytics, documenti.</p>
      {items.map((it) => (
        <div key={it.id} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto] items-center">
          <Input
            disabled={disabled}
            className={inputClass}
            value={it.url}
            onChange={(e) => update(it.id, { url: e.target.value })}
            placeholder="https://…"
          />
          <Input
            disabled={disabled}
            className={inputClass}
            value={it.label}
            onChange={(e) => update(it.id, { label: e.target.value })}
            placeholder="Etichetta breve"
          />
          <select
            disabled={disabled}
            className={cn(inputClass, "h-10 rounded-md px-2")}
            value={it.type}
            onChange={(e) => update(it.id, { type: e.target.value as TypedEvidenceLinkType })}
          >
            <option value="screenshot">Screenshot</option>
            <option value="analytics">Analytics</option>
            <option value="post">Post</option>
            <option value="landing">Landing</option>
            <option value="document">Documento</option>
            <option value="video">Video</option>
            <option value="other">Altro</option>
          </select>
          {!disabled && (
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(it.id)} aria-label="Rimuovi">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ))}
      {!disabled && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={add}
          className="bg-background text-foreground border-border hover:bg-muted hover:text-foreground"
        >
          <Plus className="h-4 w-4 mr-1" /> Aggiungi prova
        </Button>
      )}
    </div>
  );
}

function EvidenceQualityCard({ quality }: { quality: EvidenceQualityAssessment }) {
  const label =
    quality.level === "weak" ? "Debole" : quality.level === "sufficient" ? "Sufficiente" : "Solida";
  const color =
    quality.level === "weak"
      ? "border-rose-400/50 bg-rose-500/15 text-rose-100"
      : quality.level === "sufficient"
        ? "border-secondary/50 bg-secondary/25 text-slate-900"
        : "border-emerald-400/50 bg-emerald-500/15 text-emerald-100";
  const row = (ok: boolean | null, text: string) => (
    <li className="flex items-start gap-2 text-sm text-slate-100">
      {ok === true ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-300 mt-0.5 shrink-0" />
      ) : ok === false ? (
        <AlertCircle className="h-4 w-4 text-rose-300 mt-0.5 shrink-0" />
      ) : (
        <span className="h-4 w-4 mt-0.5 shrink-0 rounded-full border border-slate-400" />
      )}
      <span>{text}</span>
    </li>
  );
  return (
    <div className="rounded-xl border border-white/10 bg-slate-950/40 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold text-slate-100">Qualità delle evidenze</p>
        <span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] uppercase tracking-wider font-medium", color)}>
          {label}
        </span>
      </div>
      <ul className="space-y-1.5">
        {row(quality.checks.primary_metric_present, "Risultato principale registrato")}
        {row(quality.checks.threshold_comparable, "Soglia confrontabile con una metrica")}
        {quality.checks.sample_sufficient !== null && row(quality.checks.sample_sufficient, "Campione minimo raggiunto")}
        {row(quality.checks.qualitative_evidence_present, "Almeno una evidenza qualitativa")}
        {row(quality.checks.supporting_link_present, "Almeno una prova/link concreto")}
      </ul>
      <p className="text-xs text-slate-300">
        La valutazione è indicativa. Non decide il verdetto: lo decidi tu nello Step 5.
      </p>
    </div>
  );
}



// ---------- Step 5 — Decision gate (M6.3) ----------

const VERDICT_OPTIONS: Array<{
  v: Step5Verdict;
  label: string;
  desc: string;
  Icon: typeof ArrowRight;
  tone: string;
  ring: string;
}> = [
  {
    v: "continue",
    label: "Continua",
    desc: "I segnali sono sufficienti per proseguire con il prossimo test.",
    Icon: ArrowRight,
    tone: "text-emerald-200",
    ring:
      "border-emerald-400/50 bg-emerald-500/10 ring-1 ring-emerald-400/30",
  },
  {
    v: "iterate",
    label: "Itera",
    desc: "Esistono segnali, ma una parte del test o dell'offerta va modificata.",
    Icon: RotateCcw,
    tone: "text-amber-200",
    ring: "border-amber-400/50 bg-amber-500/10 ring-1 ring-amber-400/30",
  },
  {
    v: "stop",
    label: "Fermati",
    desc: "Le evidenze non giustificano ulteriore investimento su questa ipotesi.",
    Icon: OctagonX,
    tone: "text-rose-200",
    ring: "border-rose-400/50 bg-rose-500/10 ring-1 ring-rose-400/30",
  },
];

const CHANGE_DIMENSION_OPTIONS: Array<{ v: Step5ChangeDimension; label: string }> = [
  { v: "target", label: "Target" },
  { v: "channel", label: "Canale" },
  { v: "message", label: "Messaggio" },
  { v: "offer", label: "Offerta" },
  { v: "pricing", label: "Pricing" },
  { v: "solution", label: "Soluzione" },
  { v: "other", label: "Altro" },
];

const STOP_DISPOSITION_OPTIONS: Array<{ v: Step5StopDisposition; label: string }> = [
  { v: "pivot", label: "Pivot" },
  { v: "pause", label: "Pausa" },
  { v: "archive", label: "Archivio" },
];

function verdictLabelIT(v: Step5Verdict | null): string {
  if (v === "continue") return "Continua";
  if (v === "iterate") return "Itera";
  if (v === "stop") return "Fermati";
  return "—";
}

function qualityLabelIT(l: EvidenceQualityAssessment["level"]): string {
  return l === "strong" ? "Solide" : l === "sufficient" ? "Sufficienti" : "Deboli";
}

function formatItDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return null;
  }
}

function Step5({
  content,
  set,
  disabled,
  readOnly,
  isStepCompleted,
  saving,
  allSubmissions,
  sprintStartedAt,
  saveDraft,
  completeStep,
}: {
  content: Record<string, any>;
  set: (k: string, v: any) => void;
  disabled: boolean;
  readOnly: boolean;
  isStepCompleted: boolean;
  saving: boolean;
  allSubmissions: V2SprintSubmission[];
  sprintStartedAt: string | null;
  saveDraft: (
    patch: Record<string, any>,
    opts?: { unlockCall?: boolean },
  ) => Promise<void>;
  completeStep: (patch: Record<string, any>) => Promise<void>;
}) {
  const forcedReadOnly = readOnly || isStepCompleted;

  // ---- Source-of-truth lookup (Step 1 / 2 / 3 / 4) ----------------------
  const step1 = allSubmissions.find((s) => s.step_number === 1)?.content as any;
  const step2 = allSubmissions.find((s) => s.step_number === 2)?.content as any;
  const step3 = allSubmissions.find((s) => s.step_number === 3)?.content as any;
  const step4 = allSubmissions.find((s) => s.step_number === 4)?.content as any;
  const snapshot: ExperimentSnapshotLike | null =
    step3?.experiment_snapshot ?? null;

  const hypothesis: string =
    snapshot?.top_hypothesis ?? step1?.top_hypothesis ?? "";
  const experimentId =
    snapshot?.experiment_type ?? step2?.selected_experiment ?? "";
  const experimentLabel =
    snapshot?.experiment_label ??
    getExperimentPreset(experimentId)?.label ??
    "Esperimento custom";
  const target: string = snapshot?.target ?? step2?.target ?? "";
  const channel: string = snapshot?.channel ?? step2?.channel ?? "";
  const thresholdText: string =
    snapshot?.success_threshold ?? step2?.success_threshold ?? "";

  // Step 4 schema (preferred) with legacy fallbacks.
  const metricResults: MetricResult[] = Array.isArray(step4?.metric_results)
    ? (step4.metric_results as MetricResult[])
    : [];
  const thresholdAssessment: ThresholdAssessment | null =
    (step4?.threshold_assessment as ThresholdAssessment | null) ?? null;
  const evidenceQuality: EvidenceQualityAssessment | null =
    (step4?.evidence_quality as EvidenceQualityAssessment | null) ?? null;
  const qualitativeEvidence: QualitativeEvidence[] = Array.isArray(
    step4?.qualitative_evidence,
  )
    ? (step4.qualitative_evidence as QualitativeEvidence[])
    : [];
  const typedLinks: TypedEvidenceLink[] = Array.isArray(step4?.typed_evidence_links)
    ? (step4.typed_evidence_links as TypedEvidenceLink[])
    : [];

  // Escludi eventuali metriche "Sorgenti di traffico" (campo testuale informativo,
  // non confrontabile con la soglia) dalla selezione della metrica principale.
  const isSourceMetricLabel = (label?: string) =>
    /sorgent/i.test(label ?? "") && /traffic/i.test(label ?? "");
  const primary =
    metricResults.find((m) => m.is_primary && !isSourceMetricLabel(m.label)) ??
    metricResults.find((m) => !isSourceMetricLabel(m.label) && m.value != null) ??
    metricResults.find((m) => !isSourceMetricLabel(m.label));
  const primaryLabel = primary?.label ?? "Risultato principale";
  const primaryValue: number | null = primary?.value ?? null;

  // Sample
  const sampleTarget = parseMinimumSample(
    snapshot?.minimum_sample ?? step2?.minimum_sample,
  );
  const sampleMetric = metricResults.find((m) => m.semantic === "reached");
  const sampleCollected = sampleMetric?.value ?? null;

  // Somma contatti dal daily log di Step 3 (fonte di verità del campione).
  const step3DailyLogs: any[] = Array.isArray(step3?.daily_logs)
    ? (step3.daily_logs as any[])
    : [];

  // Threshold mapped value (fall back to primary)
  const thresholdMappedMetric =
    metricResults.find((m) => m.id === thresholdAssessment?.mapped_metric_id) ??
    primary;
  const thresholdMappedValue = thresholdMappedMetric?.value ?? primaryValue;
  const thresholdComparable =
    !!thresholdAssessment &&
    thresholdAssessment.target_value != null &&
    thresholdAssessment.mapped_metric_id != null &&
    (thresholdAssessment.parse_confidence === "high" ||
      thresholdAssessment.manually_confirmed);

  const thresholdMet: boolean | null = (() => {
    if (!thresholdAssessment || thresholdAssessment.target_value == null)
      return null;
    if (thresholdMappedValue == null) return null;
    const op = thresholdAssessment.operator ?? "gte";
    const v = thresholdMappedValue;
    const t = thresholdAssessment.target_value;
    if (op === "gte") return v >= t;
    if (op === "gt") return v > t;
    if (op === "lte") return v <= t;
    if (op === "lt") return v < t;
    if (op === "equal") return v === t;
    return v >= t;
  })();

  const evidenceLevel: EvidenceQualityAssessment["level"] =
    evidenceQuality?.level ?? "weak";

  // Interpretazione AI salvata in Step 4 (opzionale).
  const interpretationResult: {
    verdict_signal?: "green" | "yellow" | "red";
    interpretation?: string;
    bias_check?: { detected?: boolean; pattern?: string; reality?: string };
    one_line_signal?: string;
    missing_for_decision?: string[];
  } | null = (step4?.interpretation_result as any) ?? null;
  const aiSignal = interpretationResult?.verdict_signal ?? null;

  const baseSuggested: SuggestedVerdictResult = useMemo(
    () =>
      deriveSuggestedVerdict({
        primaryValue: thresholdMappedValue ?? primaryValue,
        thresholdValue: thresholdAssessment?.target_value ?? null,
        thresholdOperator: thresholdAssessment?.operator ?? null,
        thresholdComparable,
        sampleCollected,
        sampleTarget,
        evidenceLevel,
      }),
    [
      thresholdMappedValue,
      primaryValue,
      thresholdAssessment?.target_value,
      thresholdAssessment?.operator,
      thresholdComparable,
      sampleCollected,
      sampleTarget,
      evidenceLevel,
    ],
  );

  // Override del verdetto suggerito in base al segnale AI di Step 4:
  // green → continue, yellow → iterate, red → stop.
  const suggested: SuggestedVerdictResult = useMemo(() => {
    if (!aiSignal || baseSuggested.status !== "available") return baseSuggested;
    const mapped: Step5Verdict =
      aiSignal === "green" ? "continue" : aiSignal === "red" ? "stop" : "iterate";
    if (baseSuggested.verdict === mapped) return baseSuggested;
    const signalLabel =
      aiSignal === "green"
        ? "positivo"
        : aiSignal === "red"
          ? "negativo"
          : "inconclusivo";
    return {
      ...baseSuggested,
      verdict: mapped,
      reasons: [
        `Analisi dati Step 4: segnale ${signalLabel}.`,
        ...baseSuggested.reasons,
      ],
    };
  }, [baseSuggested, aiSignal]);

  // ---- Local state (mirrors content but no race) ------------------------
  const selectedVerdict: Step5Verdict | null =
    (content.selected_verdict as Step5Verdict | null) ??
    mapLegacyVerdict(content.verdict);

  const guided = {
    evidence_supporting:
      (content.guided_rationale?.evidence_supporting as string) ??
      // legacy single rationale becomes first prompt
      (typeof content.rationale === "string" ? content.rationale : ""),
    evidence_against:
      (content.guided_rationale?.evidence_against as string) ?? "",
    bias_or_uncertainty:
      (content.guided_rationale?.bias_or_uncertainty as string) ?? "",
  };

  const learning = {
    market_learning: (content.learning_capture?.market_learning as string) ?? "",
    process_learning: (content.learning_capture?.process_learning as string) ?? "",
  };

  const nextStepCont = (content.next_step_continue ?? {}) as Record<string, string>;
  const nextStepIter = (content.next_step_iterate ?? {}) as Record<string, string>;
  const nextStepStop = (content.next_step_stop ?? {}) as Record<string, string>;

  const overrideReason: string =
    (content.verdict_override_reason as string) ?? "";

  const inConflict =
    suggested.status === "available" &&
    selectedVerdict != null &&
    suggested.verdict !== selectedVerdict;

  const insufficientEvidence = suggested.status === "insufficient_evidence";

  // ---- Helpers to update structured slots -------------------------------
  const updGuided = (k: keyof typeof guided, v: string) => {
    set("guided_rationale", { ...guided, [k]: v });
  };
  const updLearning = (k: keyof typeof learning, v: string) => {
    set("learning_capture", { ...learning, [k]: v });
  };
  const updNextContinue = (k: string, v: string) =>
    set("next_step_continue", { ...nextStepCont, [k]: v });
  const updNextIterate = (k: string, v: string) =>
    set("next_step_iterate", { ...nextStepIter, [k]: v });
  const updNextStop = (k: string, v: string) =>
    set("next_step_stop", { ...nextStepStop, [k]: v });

  // ---- AI Step 5 advisor -----------------------------------------------
  type Step5AdvisorResult = {
    validation_comment: string;
    next_action: {
      what_to_change?: string;
      specific_change?: string;
      why?: string;
      next_hypothesis?: string;
      stop_reason?: string;
      pivot_hint?: string;
    };
    one_line_closure: string;
  };

  const [singleRationale, setSingleRationale] = useState<string>(
    (content.guided_rationale?.evidence_supporting as string) ??
      (typeof content.rationale === "string" ? content.rationale : "") ??
      "",
  );

  const [advisorResult, setAdvisorResult] = useState<Step5AdvisorResult | null>(
    (content.advisor_result as Step5AdvisorResult) ?? null,
  );
  const [advisorLoading, setAdvisorLoading] = useState(false);
  const [advisorError, setAdvisorError] = useState<string | null>(null);

  const runStep5Advisor = async () => {
    setAdvisorLoading(true);
    setAdvisorError(null);
    try {
      const step2Content = (allSubmissions?.find((s: any) => s.step_number === 2)?.content ?? {}) as any;
      const step4Content = (allSubmissions?.find((s: any) => s.step_number === 4)?.content ?? {}) as any;
      const interpretationResult =
        step4Content.interpretation_result ?? (content as any).interpretation_result ?? null;

      const founderVerdict =
        selectedVerdict ?? (content.verdict as "continue" | "iterate" | "stop" | undefined) ?? "iterate";

      const { data, error } = await supabase.functions.invoke("v2-sprint-step5-advisor", {
        body: {
          hypothesis: hypothesis ?? "",
          success_threshold: step2Content.success_threshold ?? thresholdText ?? "",
          target: step2Content.target ?? target ?? "",
          verdict_signal: interpretationResult?.verdict_signal ?? "yellow",
          interpretation: interpretationResult?.interpretation ?? "",
          bias_detected: interpretationResult?.bias_check?.detected ?? false,
          founder_verdict: founderVerdict,
          rationale_supporting: guided.evidence_supporting ?? "",
          rationale_against: guided.evidence_against ?? "",
        },
      });
      if (error || !data) throw new Error("advisor_failed");
      setAdvisorResult(data as Step5AdvisorResult);
      set("advisor_result", data);
    } catch {
      setAdvisorError("Non è stato possibile generare il consiglio. Riprova.");
    } finally {
      setAdvisorLoading(false);
    }
  };


  const pickNextStep = (
    v: Step5Verdict | null,
  ): Record<string, string> | null => {
    if (v === "continue") return { ...nextStepCont };
    if (v === "iterate") return { ...nextStepIter };
    if (v === "stop") return { ...nextStepStop };
    return null;
  };

  // ---- Validation -------------------------------------------------------
  const MIN_RATIONALE_CHARS = 20;
  const canConfirm =
    !!selectedVerdict &&
    singleRationale.trim().length >= 10;


  // ---- Missing-field hints (surfaced near the Confirm CTA) --------------
  const missing: string[] = [];
  if (!selectedVerdict) {
    missing.push("Scegli un verdetto (Continua, Itera o Fermati).");
  }
  if (singleRationale.trim().length < MIN_RATIONALE_CHARS) {
    missing.push(
      `Scrivi almeno una frase sul perché di questa decisione (min. ${MIN_RATIONALE_CHARS} caratteri).`,
    );
  }

  // ---- Quality warnings -------------------------------------------------
  const warnings: string[] = [];
  if (evidenceLevel === "weak")
    warnings.push("La qualità complessiva delle evidenze è ancora debole.");
  if (sampleTarget != null && sampleCollected != null && sampleCollected < sampleTarget)
    warnings.push(
      `Campione sotto il minimo: ${sampleCollected} su ${sampleTarget} previsti.`,
    );
  if (!thresholdComparable && thresholdText)
    warnings.push("La soglia dello Step 2 non è confrontabile con una metrica.");
  if (primaryValue == null)
    warnings.push("Il valore della metrica principale non è registrato.");
  if (inConflict)
    warnings.push("La tua scelta è diversa dal verdetto suggerito dai dati.");
  const topWarnings = warnings.slice(0, 3);

  // ---- Persist helpers (legacy mirrors + new schema) ---------------------
  const buildPatch = (extra: Record<string, any> = {}) => {
    const nextStep = pickNextStep(selectedVerdict);
    const patch: Record<string, any> = {
      decision_status: "draft",
      selected_verdict: selectedVerdict,
      suggested_verdict: suggested,
      verdict_override_reason: inConflict ? overrideReason : "",
      guided_rationale: {
        evidence_supporting: singleRationale,
        evidence_against: "",
        bias_or_uncertainty: "",
      },
      learning_capture: {
        market_learning: "",
        process_learning: "",
      },
      next_step_continue: nextStepCont,
      next_step_iterate: nextStepIter,
      next_step_stop: nextStepStop,
      next_step_structured: nextStep,
      decision_last_saved_at: new Date().toISOString(),
      // ---- Legacy mirrors (preserve old readers) ----
      verdict: verdictToLegacy(selectedVerdict) ?? content.verdict ?? null,
      rationale: singleRationale || buildLegacyRationale(guided, learning),
      next_step: buildLegacyNextStep(nextStep),
      threshold_recap: thresholdText || content.threshold_recap || "",
      results_recap:
        primary && primaryValue != null
          ? `${primaryLabel}: ${primaryValue}`
          : content.results_recap || "",
      ...extra,
    };
    return patch;
  };

  // ---- Draft save -------------------------------------------------------
  const [pending, setPending] = useState<null | "draft" | "complete">(null);
  const busy = saving || pending !== null;

  const handleSaveDraft = async () => {
    if (busy || forcedReadOnly) return;
    setPending("draft");
    try {
      await saveDraft(buildPatch());
      toast.success("Bozza del verdetto salvata.");
    } finally {
      setPending(null);
    }
  };

  // ---- Confirm dialog ---------------------------------------------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const handleAttemptConfirm = () => {
    if (busy || forcedReadOnly || !canConfirm) return;
    setConfirmChecked(false);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (busy || !confirmChecked) return;
    setPending("complete");
    try {
      const now = new Date().toISOString();
      const summary = buildStep5DecisionSummary({
        hypothesis,
        experimentLabel,
        target,
        channel,
        thresholdText,
        primaryLabel,
        primaryValue,
        thresholdAssessmentMet: thresholdMet,
        evidenceLevel,
        sampleCollected,
        sampleTarget,
        verdict: selectedVerdict,
        suggested,
        rationale: { evidence_supporting: singleRationale, evidence_against: "", bias_or_uncertainty: "" },
        learning: { market_learning: "", process_learning: "" },
        nextStep: pickNextStep(selectedVerdict),
        startedAt: sprintStartedAt,
        confirmedAt: now,
      });
      const patch = buildPatch({
        decision_status: "confirmed",
        decision_confirmed_at: content.decision_confirmed_at ?? now,
        decision_summary_markdown: summary,
      });
      await completeStep(patch);
      setConfirmOpen(false);
    } finally {
      setPending(null);
    }
  };

  // ---- Read-only summary copy -------------------------------------------
  const finalSummary: string | null = forcedReadOnly
    ? (content.decision_summary_markdown as string) ??
      buildStep5DecisionSummary({
        hypothesis,
        experimentLabel,
        target,
        channel,
        thresholdText,
        primaryLabel,
        primaryValue,
        thresholdAssessmentMet: thresholdMet,
        evidenceLevel,
        sampleCollected,
        sampleTarget,
        verdict: selectedVerdict,
        suggested,
        rationale: { evidence_supporting: singleRationale, evidence_against: "", bias_or_uncertainty: "" },
        learning: { market_learning: "", process_learning: "" },
        nextStep: pickNextStep(selectedVerdict),
        startedAt: sprintStartedAt,
        confirmedAt:
          (content.decision_confirmed_at as string | null) ?? null,
      })
    : null;

  const handleCopySummary = async () => {
    if (!finalSummary) return;
    try {
      await navigator.clipboard.writeText(finalSummary);
      toast.success("Riepilogo copiato negli appunti.");
    } catch {
      toast.error("Impossibile copiare. Seleziona il testo manualmente.");
    }
  };

  // =======================================================================
  // RENDER
  // =======================================================================
  return (
    <div className="space-y-5">
      <StepIntroCard text="Questo è il momento della decisione. I dati parlano — ma sei tu a scegliere cosa fare. Qui non si vince o si perde: si impara. Continui se i numeri lo giustificano, iteri se c'è un segnale parziale, ti fermi se l'ipotesi era sbagliata. Tutte e tre sono risposte valide." />
      {/* Lock badge when confirmed */}
      {isStepCompleted && (
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] uppercase tracking-wider font-medium border border-emerald-400/40 bg-emerald-500/10 text-emerald-200">
            <Lock className="h-3.5 w-3.5" /> Verdetto confermato
          </span>
          {content.decision_confirmed_at && (
            <span className="text-[11px] text-slate-300">
              Chiuso il {formatItDate(content.decision_confirmed_at as string)}
            </span>
          )}
        </div>
      )}

      {/* 1) Che cosa mostrano i dati */}
      <div className="rounded-2xl border border-secondary/30 bg-secondary/5 p-4 md:p-5 space-y-3">
        <p className="text-xs uppercase tracking-wider text-secondary font-medium">
          Che cosa mostrano i dati
        </p>
        <div className="space-y-1.5 text-sm text-slate-100">
          {hypothesis && (
            <p>
              <span className="text-slate-300">Ipotesi: </span>
              {hypothesis}
            </p>
          )}
          <p>
            <span className="text-slate-300">Esperimento: </span>
            {experimentLabel}
          </p>
        </div>

        <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 md:p-4 space-y-2">
          <p className="text-sm text-slate-100">
            <span className="text-slate-300">Risultato principale ({primaryLabel}): </span>
            <span className="font-semibold">{primaryValue ?? "—"}</span>
          </p>
          <p className="text-sm text-slate-100">
            <span className="text-slate-300">Soglia: </span>
            {thresholdText || "non definita"}
          </p>
          {thresholdMet != null && (
            <p
              className={cn(
                "text-sm",
                thresholdMet ? "text-emerald-200" : "text-amber-200",
              )}
            >
              <span className="text-slate-300">Stato: </span>
              {thresholdMet
                ? "soglia raggiunta"
                : thresholdAssessment?.target_value != null &&
                    thresholdMappedValue != null
                  ? `sotto soglia — manca ${Math.max(
                      0,
                      thresholdAssessment.target_value - thresholdMappedValue,
                    )}`
                  : "sotto soglia"}
            </p>
          )}
          {thresholdMet && aiSignal === "yellow" && (
            <p className="text-xs text-slate-400 italic leading-snug">
              La metrica usata potrebbe non corrispondere alla soglia definita — vedi analisi dati.
            </p>
          )}
          {sampleTarget != null && (() => {
            const step3ContactsSum = step3DailyLogs.reduce(
              (acc, l) => acc + (Number((l as any)?.contatti) || 0),
              0,
            );
            const sampleDisplay =
              step3ContactsSum > 0
                ? step3ContactsSum
                : (sampleCollected ?? "—");
            return (
              <p className="text-sm text-slate-100">
                <span className="text-slate-300">Campione: </span>
                {sampleDisplay} su {sampleTarget} previsti
              </p>
            );
          })()}
          <p className="text-sm text-slate-100">
            <span className="text-slate-300">Qualità evidenze: </span>
            {qualityLabelIT(evidenceLevel)}
          </p>
          {(() => {
            const quotes = qualitativeEvidence
              .map((q) => (q.quote ?? "").trim())
              .filter(Boolean)
              .slice(0, 2);
            if (quotes.length === 0) {
              return (
                <p className="text-xs text-slate-400 italic">
                  Nessuna citazione raccolta
                </p>
              );
            }
            return (
              <div className="space-y-1.5 pt-1">
                <p className="text-[11px] uppercase tracking-wider text-slate-300 font-medium">
                  Citazioni raccolte
                </p>
                {quotes.map((q, i) => (
                  <p
                    key={i}
                    className="text-xs text-slate-100 italic leading-snug border-l-2 border-slate-600/60 pl-2"
                  >
                    "{q}"
                  </p>
                ))}
              </div>
            );
          })()}
          <p className="text-[11px] text-slate-400">
            Prove/link: {typedLinks.filter((l) => (l.url ?? "").trim()).length}
          </p>

        </div>

        {/* Analisi dati AI (da Step 4) */}
        {interpretationResult && (interpretationResult.verdict_signal || interpretationResult.interpretation) && (
          <div
            className={cn(
              "rounded-xl border p-3 md:p-4 space-y-2",
              aiSignal === "green"
                ? "border-emerald-400/40 bg-emerald-500/5"
                : aiSignal === "red"
                  ? "border-rose-400/40 bg-rose-500/5"
                  : "border-amber-400/40 bg-amber-500/5",
            )}
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs uppercase tracking-wider text-slate-300 font-medium">
                Analisi dati
              </p>
              <span
                className={cn(
                  "text-xs font-semibold",
                  aiSignal === "green"
                    ? "text-emerald-200"
                    : aiSignal === "red"
                      ? "text-rose-200"
                      : "text-amber-200",
                )}
              >
                {aiSignal === "green" ? "🟢 Segnale positivo" : aiSignal === "red" ? "🔴 Segnale negativo" : "🟡 Segnale inconclusivo"}
              </span>
            </div>
            {interpretationResult.one_line_signal && (
              <p className="text-sm text-slate-100 leading-snug">
                {interpretationResult.one_line_signal}
              </p>
            )}
            {interpretationResult.interpretation && (
              <p className="text-xs text-slate-100 leading-relaxed">
                {interpretationResult.interpretation}
              </p>
            )}
            {interpretationResult.bias_check?.detected && (
              <div className="rounded-lg border border-rose-400/40 bg-rose-500/10 p-2.5 space-y-1">
                <p className="text-[11px] font-semibold text-rose-200 uppercase tracking-wider">
                  ⚠ Auto-convincimento rilevato
                </p>
                {interpretationResult.bias_check.pattern && (
                  <p className="text-xs text-slate-100">
                    <span className="text-slate-300">Pattern: </span>
                    {interpretationResult.bias_check.pattern}
                  </p>
                )}
                {interpretationResult.bias_check.reality && (
                  <p className="text-xs text-slate-100">
                    <span className="text-slate-300">Realtà: </span>
                    {interpretationResult.bias_check.reality}
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>


      {/* 2) Suggested verdict */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 md:p-5 space-y-2">
        {insufficientEvidence ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-2.5 py-1 text-[11px] uppercase tracking-wider font-medium text-amber-200">
              <Info className="h-3.5 w-3.5" /> Evidenze insufficienti
            </span>
            <p className="text-sm text-slate-100">
              Evidenze ancora insufficienti per suggerire un verdetto affidabile.
            </p>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-secondary/40 bg-secondary/10 px-2.5 py-1 text-[11px] uppercase tracking-wider font-medium text-secondary">
              <Sparkles className="h-3.5 w-3.5" /> Suggerito dai dati: {verdictLabelIT(suggested.verdict)}
            </span>
          </>
        )}
        {suggested.reasons.length > 0 && (
          <ul className="text-xs text-slate-100 space-y-1 pl-4 list-disc">
            {suggested.reasons.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        )}
        <p className="text-[11px] text-slate-300">
          Il suggerimento è deterministico e indicativo. La decisione finale resta tua.
        </p>
      </div>

      {/* 3) Recommended verdict banner */}
      {suggested.status === "available" && suggested.verdict && (
        <div className={cn(
          "rounded-xl border p-3 space-y-1",
          suggested.verdict === "continue"
            ? "border-emerald-400/30 bg-emerald-500/5"
            : suggested.verdict === "stop"
            ? "border-rose-400/30 bg-rose-500/5"
            : "border-amber-400/30 bg-amber-500/5"
        )}>
          <p className="text-xs uppercase tracking-wider text-slate-400 font-medium">
            Verdetto raccomandato dall'analisi dati
          </p>
          <p className={cn(
            "text-sm font-semibold",
            suggested.verdict === "continue" ? "text-emerald-300"
            : suggested.verdict === "stop" ? "text-rose-300"
            : "text-amber-300"
          )}>
            {verdictLabelIT(suggested.verdict)}
          </p>
          {suggested.reasons?.[0] && (
            <p className="text-xs text-slate-400 leading-snug">{suggested.reasons[0]}</p>
          )}
        </div>
      )}

      {/* 4) Verdict selection */}
      <Field label="La tua decisione">
        <div
          role="radiogroup"
          aria-label="Verdetto Sprint"
          className="grid gap-2 sm:grid-cols-3"
        >
          {VERDICT_OPTIONS.map((opt) => {
            const selected = selectedVerdict === opt.v;
            const Icon = opt.Icon;
            return (
              <button
                key={opt.v}
                type="button"
                role="radio"
                aria-checked={selected}
                aria-pressed={selected}
                disabled={disabled || forcedReadOnly}
                onClick={() => set("selected_verdict", opt.v)}
                className={cn(
                  "text-left rounded-xl border p-3.5 transition-all flex flex-col gap-1.5",
                  selected
                    ? opt.ring
                    : "border-white/10 bg-white/[0.04] hover:bg-white/[0.07]",
                  (disabled || forcedReadOnly) && "opacity-60 cursor-not-allowed",
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon className={cn("h-4 w-4", selected ? opt.tone : "text-slate-300")} />
                  <p className={cn("text-sm font-semibold", selected ? opt.tone : "text-slate-100")}>
                    {opt.label}
                  </p>
                </div>
                <p className="text-xs text-slate-300 leading-snug">{opt.desc}</p>
              </button>
            );
          })}
        </div>
      </Field>

      {/* 5) Unified rationale */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-100">
          {selectedVerdict && suggested.status === "available" && suggested.verdict !== selectedVerdict
            ? "Stai scegliendo un verdetto diverso dall'analisi. Spiega perché."
            : "Perché hai scelto questo verdetto?"}
          <span className="text-primary ml-0.5">*</span>
        </label>
        <p className="text-xs text-slate-400">
          Cosa ti ha convinto? Cosa ti preoccupa ancora?
        </p>
        {forcedReadOnly ? (
          <p className="text-sm text-slate-100 whitespace-pre-wrap rounded-xl bg-white/5 border border-white/10 px-4 py-3">
            {singleRationale || "—"}
          </p>
        ) : (
          <textarea
            value={singleRationale}
            onChange={(e) => setSingleRationale(e.target.value)}
            placeholder="Es. Il canale ha funzionato ma il messaggio non era abbastanza specifico. Procedo ma cambio l'angolo di attacco."
            rows={4}
            className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-sm placeholder:text-slate-500 focus:outline-none focus:border-primary/50 resize-none"
            disabled={forcedReadOnly}
          />
        )}
      </div>


      {/* AI Step 5 advisor */}
      {!!selectedVerdict && !disabled && !forcedReadOnly && (
        <div className="mt-6 space-y-4">
          <div className="border-t border-slate-700/50 pt-4">
            {!advisorLoading && (
              <button
                type="button"
                onClick={runStep5Advisor}
                className="w-full py-2.5 px-4 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition-colors"
              >
                {advisorResult ? "Rigenera consiglio" : "Ricevi il consiglio specifico"}
              </button>
            )}
            {advisorLoading && (
              <p className="text-center text-sm text-slate-300 py-2">Elaborazione in corso…</p>
            )}
            {advisorError && (
              <p className="text-sm text-red-400 mt-2">{advisorError}</p>
            )}
          </div>

          {advisorResult && (
            <div className="rounded-xl border border-slate-700 bg-slate-800/60 overflow-hidden">
              <div className="px-4 py-3 bg-slate-700/40 border-b border-slate-700">
                <p className="text-sm font-medium text-slate-100 italic">
                  "{advisorResult.one_line_closure}"
                </p>
              </div>

              <div className="p-4 space-y-4">
                <p className="text-sm text-slate-100 leading-relaxed">
                  {advisorResult.validation_comment}
                </p>

                {selectedVerdict === "iterate" && (
                  <div className="space-y-2">
                    {advisorResult.next_action.what_to_change && (
                      <div className="rounded-lg bg-amber-900/20 border border-amber-800/30 p-3 space-y-2">
                        <p className="text-xs font-semibold text-amber-300">Cosa cambiare</p>
                        <p className="text-sm text-slate-100">{advisorResult.next_action.what_to_change}</p>
                        {advisorResult.next_action.specific_change && (
                          <p className="text-xs text-amber-200">
                            → {advisorResult.next_action.specific_change}
                          </p>
                        )}
                        {advisorResult.next_action.why && (
                          <p className="text-xs text-slate-300">{advisorResult.next_action.why}</p>
                        )}
                      </div>
                    )}
                    {advisorResult.next_action.next_hypothesis && (
                      <div className="rounded-lg bg-slate-900/40 p-3 space-y-1">
                        <p className="text-xs font-semibold text-violet-300">Prossima ipotesi</p>
                        <p className="text-sm text-slate-100">{advisorResult.next_action.next_hypothesis}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedVerdict === "continue" && advisorResult.next_action.next_hypothesis && (
                  <div className="rounded-lg bg-emerald-900/20 border border-emerald-800/30 p-3 space-y-1">
                    <p className="text-xs font-semibold text-emerald-300">Prossima ipotesi da testare</p>
                    <p className="text-sm text-slate-100">{advisorResult.next_action.next_hypothesis}</p>
                  </div>
                )}

                {selectedVerdict === "stop" && (
                  <div className="space-y-2">
                    {advisorResult.next_action.stop_reason && (
                      <div className="rounded-lg bg-slate-900/40 p-3 space-y-1">
                        <p className="text-xs font-semibold text-slate-300">Cosa hai imparato</p>
                        <p className="text-sm text-slate-100">{advisorResult.next_action.stop_reason}</p>
                      </div>
                    )}
                    {advisorResult.next_action.pivot_hint && (
                      <div className="rounded-lg bg-violet-900/20 border border-violet-800/30 p-3 space-y-1">
                        <p className="text-xs font-semibold text-violet-300">Possibile pivot</p>
                        <p className="text-sm text-slate-100">{advisorResult.next_action.pivot_hint}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}


      {/* 7) Quality warnings */}
      {!forcedReadOnly && topWarnings.length > 0 && (
        <div className="rounded-xl border border-amber-400/30 bg-amber-500/5 p-4 space-y-2">
          <p className="text-xs uppercase tracking-wider text-amber-300 font-medium">
            Prima di chiudere lo Sprint, considera questi punti
          </p>
          <ul className="space-y-1 text-sm text-amber-100 pl-4 list-disc">
            {topWarnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 8) Missing fields — explains why Confirm is disabled */}
      {!forcedReadOnly && !canConfirm && missing.length > 0 && (
        <div className="rounded-xl border border-secondary/50 bg-slate-950/70 p-4 space-y-2 shadow-sm">
          <p className="text-xs uppercase tracking-wider text-secondary font-semibold">
            Per confermare il verdetto ti manca
          </p>
          <ul className="space-y-1.5 text-sm font-medium leading-relaxed text-slate-100 pl-4 list-disc marker:text-secondary">
            {missing.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {/* 9) CTAs */}
      {!forcedReadOnly && (
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={handleSaveDraft}
            className="border-slate-500/70 bg-slate-950/60 text-slate-100 hover:bg-slate-800/80 hover:text-slate-50 disabled:text-slate-300"
          >
            {pending === "draft" ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio…</>
            ) : (
              "Salva bozza"
            )}
          </Button>
          <Button
            type="button"
            disabled={busy || !canConfirm}
            onClick={handleAttemptConfirm}
            className="bg-primary hover:bg-primary/90"
            title={!canConfirm ? "Completa i campi indicati sopra" : undefined}
          >
            Conferma il verdetto e chiudi lo Sprint
          </Button>
        </div>
      )}

      {/* 9) Read-only summary (after confirmation) */}
      {forcedReadOnly && finalSummary && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 md:p-5 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs uppercase tracking-wider text-secondary font-medium">
              Riepilogo dello Sprint
            </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleCopySummary}
                className="border-border/80 bg-background/50 text-foreground hover:bg-background/80 hover:text-foreground font-medium"
              >
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copia riepilogo
              </Button>
          </div>
          <pre className="text-xs md:text-sm text-slate-100 whitespace-pre-wrap font-sans leading-relaxed">
            {finalSummary}
          </pre>
        </div>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi il verdetto?</AlertDialogTitle>
            <AlertDialogDescription>
              Dopo la conferma lo Sprint diventa di sola lettura. Potrai consultare
              tutti i dati, ma non modificarli.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 text-sm">
            <p>
              <span className="text-slate-300">La tua decisione: </span>
              <span className="font-semibold">{verdictLabelIT(selectedVerdict)}</span>
            </p>
            <p>
              <span className="text-slate-300">Suggerito dai dati: </span>
              {insufficientEvidence
                ? "evidenze insufficienti"
                : verdictLabelIT(suggested.verdict)}
            </p>
            <p>
              <span className="text-slate-300">Risultato vs soglia: </span>
              {primaryValue ?? "—"} {thresholdAssessment?.target_value != null
                ? `(soglia ${thresholdAssessment.target_value})`
                : ""}
            </p>
            <p>
              <span className="text-slate-300">Qualità evidenze: </span>
              {qualityLabelIT(evidenceLevel)}
            </p>
          </div>

          {(insufficientEvidence || inConflict || (sampleTarget != null && sampleCollected != null && sampleCollected < sampleTarget)) && (
            <div className="rounded-md border border-amber-400/40 bg-amber-500/10 p-3 text-xs text-amber-100 space-y-1">
              {insufficientEvidence && <p>Stai prendendo una decisione con evidenze ancora deboli o incomplete.</p>}
              {inConflict && <p>Il verdetto scelto è diverso dal suggerito dai dati.</p>}
              {sampleTarget != null && sampleCollected != null && sampleCollected < sampleTarget && (
                <p>Il campione è sotto il minimo previsto.</p>
              )}
            </div>
          )}

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <Checkbox
              checked={confirmChecked}
              onCheckedChange={(c) => setConfirmChecked(c === true)}
            />
            <span className="text-slate-200">
              Confermo che il test è concluso e che questo verdetto rappresenta la mia decisione attuale.
            </span>
          </label>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annulla</AlertDialogCancel>
            <AlertDialogAction
              disabled={busy || !confirmChecked}
              onClick={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
            >
              {pending === "complete" ? "Salvataggio…" : "Conferma e chiudi"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Date footer */}
      {forcedReadOnly && (sprintStartedAt || content.decision_confirmed_at) && (
        <p className="text-xs text-slate-300 text-center pt-1">
          {sprintStartedAt && `Sprint avviato il ${formatItDate(sprintStartedAt)}`}
          {sprintStartedAt && content.decision_confirmed_at && " · "}
          {content.decision_confirmed_at && `Chiuso il ${formatItDate(content.decision_confirmed_at as string)}`}
        </p>
      )}

      <div className="rounded-xl border border-emerald-400/20 bg-emerald-500/[0.05] px-5 py-4 space-y-3">
        <p className="text-sm font-medium text-emerald-100">
          Hai completato 7 giorni di test reali — non è poco.
        </p>
        <p className="text-sm text-slate-300 leading-relaxed">
          Qualunque cosa dicano i numeri, hai fatto qualcosa che la maggior parte delle persone con un'idea non fa mai: hai testato davvero.
        </p>
        <p className="text-sm text-slate-400">
          Vuoi un percorso più strutturato per i prossimi 30 giorni?{" "}
          <a
            href="mailto:federico@progettostartup.com"
            className="text-secondary hover:underline"
          >
            Scrivimi direttamente →
          </a>
        </p>
      </div>
    </div>

  );
}

// ---------- Step 5 sub-components ----------

function SubField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-300">{label}</Label>
      {children}
    </div>
  );
}

function RationalePrompt({
  label,
  placeholder,
  value,
  onChange,
  disabled,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  const count = value.length;
  const soft = count > 0 && count < 60;
  return (
    <div className="space-y-1">
      <Label className="text-xs font-medium text-slate-200">{label}</Label>
      <Textarea
        rows={3}
        disabled={disabled}
        className={inputClass}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {count > 0 && (
        <div className="flex items-center justify-between text-[11px]">
          <span className={cn("text-slate-300", soft && "text-amber-300")}>
            {soft ? "Prova a essere più concreto e a citare almeno un dato o un comportamento." : " "}
          </span>
          <span className="text-slate-400">{count} caratteri</span>
        </div>
      )}
    </div>
  );
}

function LearningCounter({ value, min }: { value: string; min: number }) {
  const count = value.trim().length;
  if (count === 0) return null;
  const ok = count >= min;
  return (
    <p
      className={cn(
        "text-[11px] mt-1",
        ok ? "text-slate-400" : "text-amber-300",
      )}
    >
      {ok ? `${count} caratteri` : `${count} / ${min} caratteri minimi`}
    </p>
  );
}


// ---------- Evidence links ----------
function EvidenceLinks({
  links,
  disabled,
  onChange,
}: {
  links: string[];
  disabled: boolean;
  onChange: (links: string[]) => void;
}) {
  const list = links.length ? links : [""];
  const update = (i: number, v: string) => {
    const next = [...list];
    next[i] = v;
    onChange(next);
  };
  const add = () => onChange([...list, ""]);
  const remove = (i: number) => onChange(list.filter((_, idx) => idx !== i));
  const { invalid } = validateLinks(list);

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium text-slate-100">
        Link evidenze (opzionale)
      </Label>
      <p className="text-xs text-slate-300">Screenshot pubblici, post, landing, analytics.</p>
      <div className="space-y-2">
        {list.map((v, i) => (
          <div key={i} className="flex gap-2">
            <Input
              disabled={disabled}
              className={inputClass}
              value={v}
              onChange={(e) => update(i, e.target.value)}
              placeholder="https://…"
            />
            {!disabled && list.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => remove(i)}
                aria-label="Rimuovi link"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
      </div>
      {!disabled && (
        <Button type="button" variant="outline" size="sm" onClick={add}>
          <Plus className="h-4 w-4 mr-1" /> Aggiungi link
        </Button>
      )}
      {invalid.length > 0 && (
        <p className="text-xs text-amber-300">
          Alcuni link non sembrano URL validi: {invalid.join(", ")}. Verranno comunque salvati come testo.
        </p>
      )}
    </div>
  );
}
